/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewProjectConfig.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { CustomExportButton } from "../../../components";
import ViewProject from "../../../components/customComponentsV2/projectConfig/ViewProject";
import ViewVariant from "../../../components/customComponentsV2/projectConfig/ViewVariant";
import ViewPCDetails from "../../../components/customComponentsV2/projectConfig/ViewPCDetails";
import ShowLogsDetails from "../../../components/customComponentsV2/projectConfig/ShowLogsDetails";
import MoreProjectDetails from "../../../components/customComponentsV2/projectConfig/MoreProjectDetails";
import CommonInputSwitch from "../../../components/customComponentsV2/common/InputSwitch/CommonInputSwitch";
import MoreVariantDetails from "../../../components/customComponentsV2/projectConfig/MoreVariantDetails";
import ShowVariantDetails from "../../../components/customComponentsV2/projectConfig/ShowVariantDetails";
import ShowVarientRequestDetails from "../../../components/customComponentsV2/projectConfig/ShowVarientRequestDetails";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { HandleApiActions, adminSettingsAPI, projectConfigAPI } from "../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import AddDialog from "../../../components/customComponentsV2/projectConfig/AddDialog";
import CustomFormTextField from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomMultiSelectV2 from "../../../components/customComponentsV2/common/multiselect/CustomMultiSelectV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";

import CustomDatePicker from "../../../components/customComponentsV2/common/datepicker/CustomDatePicker";
import CustomSelectVariantColor from "../../../components/customComponentsV2/common/colorpicker/CustomSelectVariantColor";
import editRequestAPI from "../../../redux/actions/editRequestApi";
import moment from "moment";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import CustomSelectV3 from "../../../components/customComponentsV2/common/select/CustomSelectV3";
import CustomDatePickerVariant from "../../../components/customComponentsV2/common/datepicker/CustomDatePickerVariant";
import CustomFormTextFieldV3 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV3";
import { showToastMessage } from "../../../redux/actions/actionHandler";
import HomologationPlanning from "../../../components/customComponentsV2/projectConfig/HomologationPlanning";
import ArchiveVariantDialog from "../../../components/customComponentsV2/projectConfig/ArchiveVariantDialog";
import { PROJECT_STATUS, archiveReasons } from "../../../config/appConstants";

const getMilestoneSequenceByOptions = (options, name) => {
  if (!Array.isArray(options)) return Number.MAX_SAFE_INTEGER;
  return (
    options.find((option) => option.value === name)?.sequence ??
    Number.MAX_SAFE_INTEGER
  );
};

const compareMilestonesBySequence = (a, b, options) =>
  getMilestoneSequenceByOptions(options, a.name) -
    getMilestoneSequenceByOptions(options, b.name) ||
  a.name.localeCompare(b.name);

const createMilestoneRow = (overrides = {}) => ({
  id:
    crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${performance.now().toString(36).replace(".", "")}`,
  name: "",
  milestoneDate: null,
  ...overrides,
    // eslint-disable-next-line react-hooks/exhaustive-deps
});

const parseMilestoneDate = (milestoneDate) => {
  if (!milestoneDate) return null;
  const parsedDate = moment(milestoneDate, ["MM-YYYY", "YYYY-MM"], true);
  if (parsedDate.isValid()) return parsedDate.startOf("month");

  const fallbackDate = moment(milestoneDate);
  return fallbackDate.isValid() ? fallbackDate.startOf("month") : null;
};

const mapProjectMilestonesToRows = (milestones = []) => {
  if (!Array.isArray(milestones) || milestones.length === 0) {
    return [];
  }

  return milestones.map((milestone) =>
    createMilestoneRow({
      name: milestone?.name || "",
      milestoneDate: parseMilestoneDate(milestone?.milestone_date),
      sequence: milestone?.sequence,
    }),
  );
};

const milestoneDatePickerSx = {
  width: "160px !important",
  height: "250px !important",
  "& .MuiPickersLayout-root": {
    minWidth: "0 !important",
    maxWidth: "160px !important",
  },
  "& .MuiPickersLayout-contentWrapper": {
    padding: 0,
  },
  "& .MuiPickersCalendarHeader-switchViewButton": {
    display: "none",
  },
  "& .MuiPickersCalendarHeader-root": {
    position: "relative",
    paddingLeft: "1px",
    paddingRight: "1px",
    justifyContent: "space-between",
    minHeight: "30px",
    marginBottom: 0,
  },
  "& .MuiPickersCalendarHeader-labelContainer": {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  "& .MuiPickersCalendarHeader-label": {
    fontSize: "14px",
  },
  "& .MuiPickersArrowSwitcher-root": {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
  "& .MuiPickersArrowSwitcher-spacer": {
    display: "none",
  },
  "& .MuiPickersArrowSwitcher-button": {
    padding: 0,
  },
  "& .MuiPickersYear-yearButton.Mui-selected": {
    backgroundColor: "#000000 !important",
    color: "#FFFFFF !important",
    "&:hover": {
      backgroundColor: "#000000 !important",
    },
    borderRadius: "0px !important",
  },
  "& .MuiPickersMonth-monthButton.Mui-selected": {
    backgroundColor: "#000000 !important",
    color: "#FFFFFF !important",
    "&:hover": {
      backgroundColor: "#000000 !important",
    },
    borderRadius: "0px !important",
  },
};


const ProjectConfigHeader = ({
  onBack,
  exportProjectDetails,
  exportProjectVariantsDetails,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 2,
    }}
  >
    <CustomButtonV2
      startIcon={<ArrowBackIcon />}
      type="submit"
      btnName="Back"
      size="small"
      handleOnClick={onBack}
    />

    <Box sx={{ display: "flex", gap: 1 }}>
      <CustomExportButton
        fileName={"Project_Configurator"}
        exportDataJson={{
          ...exportProjectDetails?.[0],
          variants: exportProjectVariantsDetails || [],
        }}
        exportDataExcel={{
          project_details:
            exportProjectDetails?.map((pr) => ({
              ...pr,
              manufacture_details: JSON.stringify(pr?.manufacture_details) || "",
              ...(pr?.zones ? { zones: JSON.stringify(pr.zones) } : {}),
              vehicle_class_details:
                JSON.stringify(pr?.vehicle_class_details) || "",
              powertrain_class_details:
                JSON.stringify(pr?.powertrain_class_details) || "",
              vehicle_function_details:
                JSON.stringify(pr?.vehicle_function_details) || "",
              delivery_market: pr?.delivery_market?.join(", ") || "",
              co_owner_details: pr?.co_owner_details?.length
                ? JSON.stringify(pr?.co_owner_details)
                : "",
              edit_user_details: pr?.edit_user_details?.length
                ? JSON.stringify(pr?.edit_user_details)
                : "",
              view_user_details: pr?.view_user_details?.length
                ? JSON.stringify(pr?.view_user_details)
                : "",
              model_year: JSON.stringify(pr?.model_year) || "",
            })) || [],
          variants_details:
            exportProjectVariantsDetails?.map((vr) => ({
              ...vr,
              powertrain_class_details:
                JSON.stringify(vr?.powertrain_class_details) || "",
              vehicle_function_details:
                JSON.stringify(vr?.vehicle_function_details) || "",
              manufacture_details:
                JSON.stringify(vr?.manufacture_details) || "",
              co_owner_details: vr?.co_owner_details?.length
                ? JSON.stringify(vr?.co_owner_details)
                : "",
              edit_user_details: vr?.edit_user_details?.length
                ? JSON.stringify(vr?.edit_user_details)
                : "",
              view_user_details: vr?.view_user_details?.length
                ? JSON.stringify(vr?.view_user_details)
                : "",
              model_year: JSON.stringify(vr?.production_year) || "",
            })) || [],
        }}
      />
    </Box>
  </Box>
);

const ProjectConfigMainLayout = ({
  tabsDataProject,
  expandPC,
  handleExpandPC,
  handleEditClick,
  logsVariant,
  expandedIndex,
  setExpandedIndex,
  variantDetailsSelected,
  expandVC,
  handleExpandVC,
  handleVariantToggle,
  handleAddVariant,
  loading,
  handleExpandVCLogs,
  expandVCLogs,
  vehicleStatusData,
  logsCountVC,
  rowsPerPageVCLogCount,
  pageVCLogCount,
  handlePageChangeLogsVC,
  isRecreatedProject,
  handleRecreateVariantClick,
  handleBackToPC,
  tabsData1,
  tableProjectConfigs,
  projectId,
  handleCopyVariant,
  handleOpenWorkflowDialog,
  projectDetailsSelected,
  setWorkflowOption,
  setArchiveTarget,
  selectedVariantDetails,
  setSelectedVariantsToArchive,
  setOpenArchiveVariantConfirmDialog,
  renderBeforeZones,
}) => {
  let variantArchiveReasons = [];
  if (Array.isArray(selectedVariantDetails?.archive_reason)) {
    variantArchiveReasons = selectedVariantDetails.archive_reason;
  } else if (selectedVariantDetails?.archive_reason) {
    variantArchiveReasons = [selectedVariantDetails.archive_reason];
  }

  const hasMissingVariantArchiveReasons = Boolean(
    String(selectedVariantDetails?.archive_status)?.toUpperCase() === "ARCHIVED" &&
      variantArchiveReasons.some(
        (r) => !archiveReasons?.variant_exceptionToActive?.includes(r)
      )
  );

  return (
    <Grid container>
    <Grid item xs={12} md={5}>
      <Box
        sx={{
          borderRadius: 0,
          minHeight: "600px",
          maxHeight: "600px",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#a0a0a0",
          },
        }}
      >
        <ViewProject
          tabsData={tabsDataProject}
          expanded={expandPC}
          setExpanded={handleExpandPC}
        />
        <ViewVariant
          handleEditClick={handleEditClick}
          logs={logsVariant}
          expandedIndex={expandedIndex}
          setExpandedIndex={setExpandedIndex}
          variantDetailsSelected={variantDetailsSelected}
          expanded={expandVC}
          setExpanded={handleExpandVC}
          handleVariantToggle={handleVariantToggle}
          handleAddVariant={handleAddVariant}
          loading={loading}
          setExpandVCLogs={handleExpandVCLogs}
          expandVCLogs={expandVCLogs}
          vehicleStatusData={vehicleStatusData}
          logsCount={logsCountVC}
          rowsPerPageVCLogCount={rowsPerPageVCLogCount}
          pageVCLogCount={pageVCLogCount}
          handlePageChangeLogsVC={handlePageChangeLogsVC}
          isRecreatedProject={isRecreatedProject}
          onRecreateVariantClick={handleRecreateVariantClick}
          onCopyVariant={handleCopyVariant}
          projectStatus={projectDetailsSelected?.status}
          projectArchiveStatus={projectDetailsSelected?.archive_status}
          archive_status={projectDetailsSelected?.archive_status}
          archive_reason={projectDetailsSelected?.archive_reason}
        />
      </Box>
    </Grid>
    <Grid item xs={12} md={7}>
      <Box
        sx={{
          borderRadius: 0,
          minHeight: "600px",
          maxHeight: "600px",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#a0a0a0",
          },
        }}
      >
        <Box>
          {expandVC ? (
            <>
              <CustomButtonV2
                startIcon={<ArrowBackIcon />}
                type="submit"
                size="small"
                handleOnClick={handleBackToPC}
              />
              <Box sx={{ mt: 2 }}>
                <MoreVariantDetails 
                  tabsData={tabsData1} 
                  renderComponentBelow={
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1, mt: 1, mr: 1 }}>
                      <CustomButtonV2
                        btnName={selectedVariantDetails?.archive_status === "ARCHIVED" ? "Retrieve" : "Archive"}
                        disabled={hasMissingVariantArchiveReasons}
                        handleOnClick={() => {
                          setWorkflowOption(selectedVariantDetails?.archive_status === "ARCHIVED" ? "retrieve" : "archive");
                          setArchiveTarget("variant");
                          setSelectedVariantsToArchive([selectedVariantDetails?.variant_id]);
                          setOpenArchiveVariantConfirmDialog(true);
                        }}
                      />
                    </Box>
                  } 
                />
              </Box>
            </>
          ) : (
            <CustomTabsV2
              tabs={[
                {
                  name: "Details",
                  count: tableProjectConfigs?.length || "",
                  panelPadding: "8px 16px 16px 16px",
                  content: (
                    <MoreProjectDetails
                      tableProjectConfigs={tableProjectConfigs}
                      renderBeforeZones={renderBeforeZones}
                    />
                  ),
                },
                {
                  name: "Homologation Planning",
                  count: "",
                  content: (
                    <HomologationPlanning projectId={projectId} />
                  ),
                },
              ]}
            />
          )}
        </Box>
      </Box>
    </Grid>
  </Grid>
  );
};

const buildLabelValueOptions = (items, getLabel, getValue) =>
  (items || []).map((item) => ({
    label: getLabel(item),
    value: getValue(item),
  }));

const addBatchMilestones = (prev, milestonesToAdd) => {
  if (milestonesToAdd.some((m) => !m.name || !m.milestoneDate)) {
    return prev;
  }
  const existingNames = new Set(prev.map((row) => row.name));
  return [
    ...prev,
    ...milestonesToAdd.filter((m) => !existingNames.has(m.name)),
  ];
};

const addSingleMilestoneFromDraft = (prev, draft, milestoneNameOptions) => {
  const trimmedName = draft.name?.trim();
  if (
    !trimmedName ||
    trimmedName.length < 3 ||
    !draft.milestoneDate ||
    prev.some((m) => m.name === trimmedName)
  ) {
    return prev;
  }

  const selectedMilestoneOption = milestoneNameOptions?.find(
    (option) => option.value === trimmedName,
  );

  return [
    ...prev,
    createMilestoneRow({
      name: trimmedName,
      milestoneDate: draft.milestoneDate,
      sequence: selectedMilestoneOption?.sequence,
    }),
  ];
};

const removeMilestoneById = (prev, id) => prev.filter((row) => row.id !== id);

const createAddMilestoneHandler = (setRows, setDraft, draft, milestoneNameOptions) => (milestonesToAdd = []) => {
  if (Array.isArray(milestonesToAdd) && milestonesToAdd.length > 0) {
    setRows((prev) => addBatchMilestones(prev, milestonesToAdd));
    setDraft(createMilestoneRow());
    return;
  }

  setRows((prev) => addSingleMilestoneFromDraft(prev, draft, milestoneNameOptions));
  setDraft(createMilestoneRow());
};

const createRemoveMilestoneHandler = (setRows) => (id) =>
  setRows((prev) => removeMilestoneById(prev, id));

const getSelectedValueIds = (selectedValuesUpdateProject, title) =>
  selectedValuesUpdateProject?.[title]?.map((data) => data?.value) || [];

const filterOutSelectedIds = (options, selectedIds) =>
  options.filter(
    (option) =>
      !selectedIds.some((selectedId) =>
        typeof option.value === "object"
          ? JSON.stringify(option?.value) === JSON.stringify(selectedId)
          : option.value === selectedId,
      ),
  );

const isOptionSelected = (option, selected) => {
  if (typeof option.value === "object") {
    return (
      JSON.stringify(option?.value) === JSON.stringify(selected?.value)
    );
  }
  return option?.value === selected?.value;
};

const areSelectionArraysEqual = (arr1 = [], arr2 = []) => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((item1) => arr2.some((item2) => isOptionSelected(item1, item2)));
};

const getNonEmptyUsers = (users = []) =>
  (users || []).filter((u) => Object.keys(u || {}).length > 0);

const getUserDisplayName = (user = {}) =>
  user?.fullname || user?.name || user?.email || "-";

const getUniqueUsersByNameEmail = (users = []) =>
  [
    ...new Map(
      (users || []).map((user) => [
        `${getUserDisplayName(user)}-${user?.email || "NO_EMAIL"}`,
        user,
      ]),
    ).values(),
  ];

const renderUserTable = (users, widthFallback) => (
  <Table
    stickyHeader
    size="small"
    sx={{
      tableLayout: "fixed",
      width: "100%",
    }}
  >
    <TableBody>
      {users.length > 0 ? (
        users.map((user) => {
          const displayName = getUserDisplayName(user);
          const displayEmail = user?.email || "-";
          return (
            <TableRow key={user?.user_id}>
              <TableCell sx={{ width: user?.width || widthFallback }}>
                {`${displayName} - ${displayEmail}`}
              </TableCell>
            </TableRow>
          );
        })
      ) : (
        <TableRow>
          <TableCell align="center" sx={{ py: 2 }}>
            No data available
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
);

const buildTabsDataUsersPC = (projectDetailsSelected) => {
  const ownerUsers = Array.from(
    getUniqueUsersByNameEmail(projectDetailsSelected?.owner_details || []),
  );
  const editorUsers = [
    ...getNonEmptyUsers(projectDetailsSelected?.co_owner_details),
    ...getNonEmptyUsers(projectDetailsSelected?.edit_user_details),
  ];
  const viewerUsers = getNonEmptyUsers(projectDetailsSelected?.view_user_details);

  return [
    {
      name: "Owner",
      count: ownerUsers.length || 0,
      content: renderUserTable(ownerUsers, "150px"),
    },
    {
      name: "Editor",
      count: editorUsers.length || 0,
      content: renderUserTable(editorUsers, "200px"),
    },
    {
      name: "Viewer",
      count: viewerUsers.length || 0,
      content: renderUserTable(viewerUsers, "150px"),
    },
  ];
};

const buildTabsDataUsersVC = (selectedVariantDetails) => {
  const ownerUsers = Array.from(
    getUniqueUsersByNameEmail(
      getNonEmptyUsers(selectedVariantDetails?.owner_details),
    ),
  );
  const editorUsers = [
    ...getNonEmptyUsers(selectedVariantDetails?.co_owner_details),
    ...getNonEmptyUsers(selectedVariantDetails?.edit_user_details),
  ];
  const viewerUsers = getNonEmptyUsers(selectedVariantDetails?.view_user_details);

  return [
    {
      name: "Owner",
      count: ownerUsers.length || 0,
      content: renderUserTable(ownerUsers, "150px"),
    },
    {
      name: "Editor",
      count: editorUsers.length || 0,
      content: renderUserTable(editorUsers, "200px"),
    },
    {
      name: "Viewer",
      count: viewerUsers.length || 0,
      content: renderUserTable(viewerUsers, "150px"),
    },
  ];
};

const MilestoneSection = ({
    rows,
    draft,
    onAddMilestone,
    onRemoveMilestone,
  milestoneNameOptions = [],
  }) => {
    const safeMilestoneNameOptions = Array.isArray(milestoneNameOptions) ? milestoneNameOptions : [];
    const [datePickerAnchor, setDatePickerAnchor] = useState(null);
    const [datePickerView, setDatePickerView] = useState("year");
    const [pendingMilestoneDate, setPendingMilestoneDate] = useState(null);

    const [activeMilestoneName, setActiveMilestoneName] = useState("");

    const milestoneDates = draft?.milestoneDates || {};

    const handleOpenDatePicker = (event, milestoneName) => {
      setDatePickerAnchor(event.currentTarget);
      setActiveMilestoneName(milestoneName);
      setPendingMilestoneDate(milestoneDates[milestoneName] || null);
      setDatePickerView("year");
    };

    const handleCloseDatePicker = () => {
      setDatePickerAnchor(null);
      setActiveMilestoneName("");
      setPendingMilestoneDate(null);
      setDatePickerView("year");
    };

    const updateMilestoneDate = (date) => {
      if (!activeMilestoneName) return;

      const selectedMilestoneOption = safeMilestoneNameOptions.find(
        (option) => option.value === activeMilestoneName,
      );

      onAddMilestone([
        createMilestoneRow({
          name: activeMilestoneName,
          milestoneDate: date,
          sequence: selectedMilestoneOption?.sequence,
        }),
      ]);
    };

    const handleSelectedPickerClick = (event) => {
      const selectedButton = event.target.closest("button.Mui-selected");
      if (!selectedButton) return;

      const selectedText = selectedButton.textContent?.trim();
      const baseDate =
        pendingMilestoneDate || milestoneDates[activeMilestoneName] || moment();

      if (datePickerView === "year" && /^\d{4}$/.test(selectedText)) {
        setPendingMilestoneDate(
          baseDate.clone().year(Number(selectedText)).startOf("month"),
        );
        setDatePickerView("month");
        return;
      }

      if (datePickerView === "month") {
        const monthIndex = [...moment.monthsShort(), ...moment.months()]
          .findIndex(
            (month) => month.toLowerCase() === selectedText?.toLowerCase(),
          ) % 12;

        if (monthIndex === -1) return;

        updateMilestoneDate(baseDate.clone().month(monthIndex).startOf("month"));
        handleCloseDatePicker();
      }
    };

    const addedMilestoneNames = new Set(
      (rows || []).map((milestone) => milestone.name?.trim()).filter(Boolean),
    );
    const availableMilestoneNameOptions = safeMilestoneNameOptions.filter(
      (option) => !addedMilestoneNames.has(option.value),
    );
    const milestoneDropdownOptions =
      availableMilestoneNameOptions.length > 0
        ? availableMilestoneNameOptions
        : [{ label: "No data", value: "__no_data__" }];
    const sortedRows = rows
      .slice()
      .sort((a, b) => compareMilestonesBySequence(a, b, safeMilestoneNameOptions));

    return (
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Milestones</Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            alignItems: "end",
          }}
        >
          {milestoneDropdownOptions.map((option) => (
            <React.Fragment key={option.value}>
              <CustomFormTextFieldV3
                label="Milestone Name"
                required
                value={option.value === "__no_data__" ? "" : option.value}
                placeholder="No data"
                disabled
              />
              <Box>
                <Typography
                  sx={{
                    mb: 0.5,
                    color: "#6f7174",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                  }}
                >
                  Milestone Date <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="MM-YYYY"
                  value={milestoneDates[option.value]?.format("MM-YYYY") || ""}
                  onClick={(event) => {
                    if (option.value === "__no_data__") return;
                    handleOpenDatePicker(event, option.value);
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <CalendarMonthIcon
                        sx={{
                          fontSize: 18,
                          color: "#000",
                          cursor: "pointer",
                        }}
                      />
                    ),
                  }}
                  sx={{
                    cursor:
                      option.value === "__no_data__" ? "not-allowed" : "pointer",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                      "& fieldset": {
                        borderColor: "#d9d9d9",
                        borderRadius: "0px",
                      },
                      "&:hover fieldset": { borderColor: "#8c8c8c" },
                      "&.Mui-focused fieldset": { borderColor: "#000" },
                    },
                  }}
                />
              </Box>
            </React.Fragment>
          ))}
        </Box>

        {rows.length > 0 ? (
          <Stack
            direction="row"
            mt={2}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              maxHeight: 72,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              pb: 0.5,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#bfbfbf",
                borderRadius: 10,
              },
            }}
          >
            {sortedRows.map((milestone) => (
              <Chip
                key={milestone.id}
                label={`${milestone.name} - ${milestone.milestoneDate?.format("MM-YYYY")}`}
                onDelete={() => onRemoveMilestone(milestone.id)}
                sx={{
                  backgroundColor: "#000",
                  color: "white",
                  borderRadius: 0,
                  px: 1.2,
                  py: 0.5,
                  mr: 1.2,
                  "& .MuiChip-deleteIcon": {
                    color: "white",
                    fontSize: 18,
                    "&:hover": {
                      color: "white",
                    },
                  },
                }}
              />
            ))}
          </Stack>
        ) : null}

        <Popover
          open={Boolean(datePickerAnchor)}
          anchorEl={datePickerAnchor}
          onClose={handleCloseDatePicker}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: { overflow: "visible", borderRadius: "0px" } } }}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box onClickCapture={handleSelectedPickerClick}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                value={
                  pendingMilestoneDate ||
                  milestoneDates[activeMilestoneName] ||
                  null
                }
                onChange={(newValue) => {
                  if (!newValue) return;
                  if (datePickerView === "year") {
                    setPendingMilestoneDate(newValue.startOf("month"));
                    setDatePickerView("month");
                    return;
                  }
                  updateMilestoneDate(newValue.startOf("month"));
                  handleCloseDatePicker();
                }}
                views={["year", "month"]}
                view={datePickerView}
                slotProps={{ actionBar: { actions: [] } }}
                sx={milestoneDatePickerSx}
              />
            </Box>
          </LocalizationProvider>
        </Popover>
      </Box>
    );
  };

const fetchProjectDetailsAPI = async (dispatch, params = {}) => {
  try {
    const {
      project_id,
      name,
      trade_mark,
      status,
      model_year,
      is_zonal,
      page_number,
      page_size,
      order_by_sort,
      order_by_column,
    } = params;

    const queryParams = {
      page_number,
      ...(page_size && { page_size }),
      ...(project_id && { project_id }),
      ...(name && { name }),
      ...(trade_mark && { trade_mark }),
      ...(status && { status }),
      ...(model_year && { model_year }),
      ...(is_zonal && { is_zonal }),
      ...(order_by_sort && { order_by_sort }),
      ...(order_by_column && { order_by_column }),
    };

    return await Promise.resolve(dispatch(
      HandleApiActions({
        ...projectConfigAPI.get_project,
        params: queryParams,
      })
    ));
  } catch (error) {
    console.error("Error fetching project details:", error);
  }
};

const fetchVariantDetailsAPI = async (dispatch, params = {}) => {
  try {
    const {
      project_id,
      variant_id,
      name,
      delivery_market,
      is_complete_details_required,
      sort_column,
      sort_order,
      is_for_access,
    } = params;

    const queryParams = {
      project_id,
      is_complete_details_required,
      ...(variant_id && { variant_id }),
      ...(name && { name }),
      ...(delivery_market && { delivery_market }),
      ...(sort_column && { sort_column }),
      ...(sort_order && { sort_order }),
      ...(is_for_access && { is_for_access }),
    };

    return await Promise.resolve(dispatch(
      HandleApiActions({
        ...projectConfigAPI.get_variant_details,
        params: queryParams,
      })
    ));
  } catch (error) {
    console.error("❌ Error fetching variant details:", error.message);
    throw error;
  }
};

const buildProjectStatusOptionList = (projectStatusMeta, isCertificateUploaded) => {
  if (!projectStatusMeta) return [];
  return Object.entries(projectStatusMeta).map(([key, value]) => {
    const isComplete =
      value === 500 ||
      value === "500" ||
      value === PROJECT_STATUS?.COMPLETE ||
      key?.trim()?.toUpperCase() === "COMPLETE";
    return {
      label: key.replaceAll("_", " "),
      value,
      disabled: isComplete ? !isCertificateUploaded : false,
    };
  });
};

const getStatusText = (status) => {
  if (status === 100 || status === "100" || status === "A") return "Approved";
  if (status === 200 || status === "200" || status === "R") return "Rejected";
  return "Pending";
};

const getProjectMilestoneSequence = (milestone, index, vehicleStatusList = []) =>
  milestone?.sequence ??
  vehicleStatusList.find((item) => item?.name === milestone?.name)?.sequence ??
  index;

const buildMilestoneDetailsRows = (milestones = [], vehicleStatusList = []) =>
  (milestones || [])
    .slice()
    .sort(
      (a, b) =>
        getProjectMilestoneSequence(a, milestones?.indexOf(a), vehicleStatusList) -
          getProjectMilestoneSequence(b, milestones?.indexOf(b), vehicleStatusList) ||
        (a?.name || "").localeCompare(b?.name || ""),
    )
    .map((data) => ({
      milestone_name: data?.name,
      milestone_date:
        parseMilestoneDate(data?.milestone_date)?.format("MM-YYYY") ||
        data?.milestone_date,
    }));

const getMilestoneNameOptions = (projectMilestones = [], vehicleStatusList = []) => {
  let options = [
    ...new Map(
      (projectMilestones || [])
        .filter((milestone) => milestone?.name)
        .map((milestone, index) => [
          milestone.name,
          {
            label: milestone.name,
            value: milestone.name,
            sequence:
              milestone.sequence ??
              vehicleStatusList.find((item) => item?.name === milestone.name)
                ?.sequence ??
              index,
          },
        ]),
    ).values(),
  ];

  if (options.length === 0) {
    options = vehicleStatusList.slice().map((item) => ({
      label: item?.name,
      value: item?.name,
      sequence: item?.sequence,
    }));
  }

  return options.sort(
    (a, b) =>
      (a.sequence ?? Number.MAX_SAFE_INTEGER) -
        (b.sequence ?? Number.MAX_SAFE_INTEGER) ||
      a.label.localeCompare(b.label),
  );
};

const mapUserDetailsExport = (users) => {
  if (!Array.isArray(users)) return [];
  return users
    .filter((us) => us && Object.keys(us).length > 0)
    .map((us) => ({
      user_id: us?.user_id || null,
      name: us?.name || "",
      email: us?.email || "",
    }));
};

const mapUserDetailsForForm = (users) => {
  if (users?.[0]?.user_id) {
    return users.map((item) => ({
      label: item.name,
      subLabel: item.email,
      value: item?.user_id,
    }));
  }
  return [];
};

const getFirstItem = (item) => (Array.isArray(item) ? item[0] : item);

const findMatchedManufacturer = (mftDetails, selectedMft) => {
  if (!mftDetails?.length || !selectedMft) return null;
  return mftDetails.find((item) => item.name === selectedMft?.name) || null;
};

const resolveWorkflowDialogTarget = (workflowOption, isProjectArchived) => {
  if (workflowOption === "archive") {
    return isProjectArchived ? "variant" : "project";
  }
  return isProjectArchived ? "project" : "variant";
};

const mapEditRequestRows = (data = []) =>
  (data || [])
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map((item) => ({
      id: item.er_id,
      status: item.status,
      status_data: getStatusText(item.status),
      requested_at: new Date(item.created_at).toLocaleString(),
      requested_user_details: {
        fullname: item.requested_user?.fullname || "-",
        email: item.requested_user?.email || "-",
      },
      requested_reason: item.request_reason || "-",
      approved_rejected_by: item.approved_user?.fullname || "-",
      approved_rejected_reason: item.update_reason || "-",
      ee_details: item.ee_details || null,
      swm_details: item.swm_details || null,
    }));

const buildProjectUpdateReqObj = (projectId, title, editedValue, selectedValuesUpdateProject) => ({
  project_id: Number.parseInt(projectId),
  ...(title === "TRADEMARK" && { trade_mark: editedValue }),
  ...(title === "VEHICLE FUNCTION(PROJECT)" && {
    vehicle_funcs: selectedValuesUpdateProject[title]?.map((d) => d?.value),
  }),
  ...(title === "POWERTRAIN CLASS" && {
    pwrtrn_class: selectedValuesUpdateProject[title]?.map((d) => d?.value),
  }),
  ...(title === "MANUFACTURER CONFIGURATIONS" && {
    mft_details: selectedValuesUpdateProject[title]?.map((d) => d?.value),
  }),
  ...(title === "DELIVERY MARKET" && {
    delivery_market: selectedValuesUpdateProject[title]?.map((d) => d?.value),
  }),
  current_time: moment().format(),
});

const updateProjectDetailsByTitle = (prev, title, editedValue, selectedValuesUpdateProject) => {
  switch (title) {
    case "TRADEMARK":
      return { ...prev, trade_mark: editedValue };
    case "VEHICLE FUNCTION(PROJECT)":
      return {
        ...prev,
        vehicle_funcs: selectedValuesUpdateProject[title]?.map((d) => d?.value),
      };
    case "POWER TRAIN CLASSES":
    case "POWERTRAIN CLASS":
      return {
        ...prev,
        pwrtrn_class: selectedValuesUpdateProject[title]?.map((d) => d?.value),
      };
    case "MANUFACTURER CONFIGURATIONS":
      return {
        ...prev,
        mft_details: selectedValuesUpdateProject[title]?.map((d) => d?.value),
      };
    case "DELIVERY MARKET":
      return {
        ...prev,
        delivery_market: selectedValuesUpdateProject[title]?.map((d) => d?.value),
      };
    default:
      return prev;
  }
};

const buildSelectedValuesUpdateProject = (projectDetailsSelected, selectedVariantDetails) => ({
  "VEHICLE FUNCTION(PROJECT)":
    projectDetailsSelected?.vehicle_function_details?.map((item) => ({
      label: `${item.name} (${item.abbreviation})`,
      value: item?.id,
    })) || [],
  "VEHICLE FUNCTION(VARIANT)":
    selectedVariantDetails?.vehicle_function_details?.map((item) => ({
      label: `${item.name} (${item.abbreviation})`,
      value: item?.id,
    })) || [],
  "POWERTRAIN CLASS":
    projectDetailsSelected?.powertrain_class_details?.map((item) => ({
      label: `${item.name} (${item.abbreviation})`,
      value: item?.id,
    })) || [],
  "DELIVERY MARKET":
    projectDetailsSelected?.delivery_market?.map((item) => ({
      label: item,
      value: item,
    })) || [],
  "MANUFACTURER CONFIGURATIONS":
    projectDetailsSelected?.mft_details?.map((item) => ({
      label: item?.name,
      subLabel: item?.address,
      value: item,
    })) || [],
});

const buildFilteredProjectExportDetails = (projectDetailsSelected) =>
  [projectDetailsSelected]?.map((pr) => ({
    name: pr?.name || "",
    trade_mark: pr?.trade_mark || "",
    vehicle_platform: pr?.vehicle_platform || "",
    status: pr?.status || "",
    architecture_name: pr?.architecture_name || "",
    manufacture_details: pr?.mft_details || [],
    mft_details: pr?.mft_details || [],
    vehicle_class: pr?.vehicle_class || pr?.vehicle_class_details?.[0]?.id || null,
    pwrtrn_class: pr?.pwrtrn_class || pr?.powertrain_class_details?.map((pc) => pc?.id) || [],
    vehicle_funcs: pr?.vehicle_funcs || pr?.vehicle_function_details?.map((vf) => vf?.id) || [],
    current_time: pr?.current_time || new Date().toISOString(),
    ...(pr?.is_zonal ? { zones: pr.project_zones } : {}),
    vehicle_class_details:
      pr?.vehicle_class_details?.map((vc) => ({
        id: vc?.id,
        name: vc?.name,
      }))?.[0] || null,
    powertrain_class_details:
      pr?.powertrain_class_details?.map((pc) => ({
        id: pc?.id,
        name: pc?.name,
      })) || [],
    vehicle_function_details:
      pr?.vehicle_function_details?.map((vf) => ({
        id: vf?.id,
        name: vf?.name,
      })) || [],
    country: pr?.country || "",
    region: pr?.region || "",
    delivery_market: pr?.delivery_market || [],
    co_owners: pr?.co_owners || pr?.co_owner_details?.map((u) => u?.user_id || u?.id).filter(Boolean) || [],
    target_market: pr?.target_market || "",
    vehicle_type: pr?.vehicle_type || "",
    no_of_seats: pr?.no_of_seats || "",
    model_year: pr?.model_year || "",
    milestones: pr?.milestones || [],
    co_owner_details: mapUserDetailsExport(pr?.co_owner_details),
    edit_user_details: mapUserDetailsExport(pr?.edit_user_details),
    view_user_details: mapUserDetailsExport(pr?.view_user_details),
  }));

const buildFilteredVariantExportDetails = (variantDetailsSelected) =>
  Array.isArray(variantDetailsSelected)
    ? variantDetailsSelected
      .filter((vr) => vr && typeof vr === "object")
      .map((vr) => ({
        name: typeof vr?.name === "string" ? vr.name.trim() : "",
        color_code: typeof vr?.color_code === "string" ? vr.color_code : "",
        delivery_market:
          typeof vr?.delivery_market === "string" ? vr.delivery_market : "",

        powertrain_class_details:
          Array.isArray(vr?.powertrain_class_details) &&
            vr.powertrain_class_details.length > 0 &&
            vr.powertrain_class_details[0]?.id
            ? {
              id: vr.powertrain_class_details[0].id,
              name: vr.powertrain_class_details[0]?.name || "",
            }
            : null,

        vehicle_status:
          typeof vr?.vehicle_status === "string" ? vr.vehicle_status : "",

        vehicle_function_details: Array.isArray(
          vr?.vehicle_function_details,
        )
          ? vr.vehicle_function_details
            .filter((vf) => vf?.id)
            .map((vf) => ({
              id: vf.id,
              name: vf?.name || "",
            }))
          : [],

        manufacture_details:
          vr?.mft_details && Object.keys(vr.mft_details)?.length
            ? vr.mft_details
            : null,

        co_owner_details: mapUserDetailsExport(vr?.co_owner_details),

        edit_user_details: mapUserDetailsExport(vr?.edit_user_details),

        view_user_details: mapUserDetailsExport(vr?.view_user_details),

        production_year: vr?.production_year || null,
      }))
    : [];

const resolveVehicleStatusList = (data) =>
  Array.isArray(data) ? data : data?.data || [];

const resolveOriginalMilestones = (
  target,
  selectedVariantDetails,
  projectDetailsSelected,
) =>
  target === "variant"
    ? selectedVariantDetails?.milestones || projectDetailsSelected?.milestones
    : projectDetailsSelected?.milestones;

const renderUserSelectOption = (props, option) => (
  <li {...props} key={option.value}>
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Typography sx={{ fontWeight: 400 }}>{option.label}</Typography>
      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
        {option.subLabel}
      </Typography>
    </Box>
  </li>
);

const getUserOptionsList = (usersListData) =>
  usersListData?.length > 0
    ? usersListData.map((item) => ({
      label: item.fullname,
      subLabel: item.email,
      value: item?.user_id,
    }))
    : [];

const buildVariantFormLayout = ({
  openRecreationVariant,
  selectedVariantForRecreation,
  setSelectedVariantForRecreation,
  setFormStates,
  recreationVariantsList,
  variantName,
  setVariantName,
  openAddVariant,
  milestoneSectionProps,
  modelYear,
  setModelYear,
  variantColor,
  setVariantColor,
  deliveryMarkets,
  setDeliveryMarkets,
  projectDetailsSelected,
  vehicleFunctions,
  setVehicleFunctions,
  powertrainClasses,
  setPowertrainClasses,
  manufacturerDetails,
  setManufacturerDetails,
  coOwners,
  setCoOwners,
  editors,
  setEditors,
  viewers,
  setViewers,
  usersListData,
}) => {
  const userOptions = getUserOptionsList(usersListData);

  return [
    ...(openRecreationVariant
      ? [
        {
          component: "CustomSelectV3",
          props: {
            placeholder: "Select Parent Variant",
            label: "Parent Variant",
            required: true,
            selectedValue: selectedVariantForRecreation,
            handleOnChange: (e) => {
              setSelectedVariantForRecreation(e.target.value);
              setFormStates(e.target.value);
            },

            optionsList:
              recreationVariantsList.map((item) => ({
                label: item?.name,
                value: item,
              })) || [],
          },
        },
      ]
      : []),
    {
      component: "CustomFormTextFieldV3",
      props: {
        label: "Variant Name",
        required: true,
        type: "text",
        fldType: "text",
        value: variantName,
        setValue: setVariantName,
        placeholder: "Enter Variant Name",
        infoname: "variant_name",
      },
    },
    ...(openAddVariant || openRecreationVariant
      ? [
        {
          component: "MilestoneSection",
          props: milestoneSectionProps,
        },
      ]
      : []),
    {
      component: "CustomDatePickerVariant",
      props: {
        label: "Select a Model Year",
        required: true,
        value: modelYear,
        onChange: setModelYear,
      },
    },
    {
      component: "CustomSelectVariantColor",
      props: {
        label: "Variant Color",
        infoname: "variant_color",
        required: true,
        value: variantColor,
        setValue: setVariantColor,
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select Delivery Market",
        label: "Delivery Market",
        infoname: "delivery_market",
        required: true,
        selectedValue: deliveryMarkets,
        handleOnChange: (e) => setDeliveryMarkets(e.target.value),

        optionsList:
          projectDetailsSelected?.delivery_market?.map((item) => ({
            label: item,
            value: item,
          })) || [],
      },
    },
    {
      component: "CustomMultiSelectV2",
      props: {
        label: "Vehicle Function",
        required: true,
        infoname: "vehicle_function",
        selectedValues: vehicleFunctions,
        setSelectedValues: setVehicleFunctions,
        size: "small",
        optionsList:
          projectDetailsSelected?.vehicle_function_details?.map((item) => ({
            label: `${item.name} (${item.abbreviation})`,
            value: item?.id,
          })) || [],
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select PowertrainClass",
        label: "Powertrain Class",
        infoname: "power_train_class",
        required: true,
        selectedValues: powertrainClasses,
        setSelectedValues: setPowertrainClasses,
        selectedValue: powertrainClasses,
        handleOnChange: (e) => setPowertrainClasses(e.target.value),
        size: "small",
        optionsList:
          projectDetailsSelected?.powertrain_class_details?.map((item) => ({
            label: `${item.name} (${item.abbreviation})`,
            value: item?.id,
          })) || [],
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select Manufacturer Details",
        label: "Manufacturer Details",
        infoname: "manufacturer_name",
        required: true,
        selectedValue: manufacturerDetails,
        handleOnChange: (e) => {
          console.log(
            "---setManufacturerDetails(e.target.value)---",
            e.target.value,
            projectDetailsSelected?.mft_details,
          );
          setManufacturerDetails(e.target.value);
        },

        optionsList:
          projectDetailsSelected?.mft_details?.map((item) => ({
            label: item?.name,
            subLabel: item?.address,
            value: item,
          })) || [],
      },
    },
    ...(openRecreationVariant
      ? [
        {
          component: "CustomMultiSelectV2",
          props: {
            label: "Co Owners",
            required: false,
            selectedValues: coOwners,
            setSelectedValues: setCoOwners,
            optionsList: userOptions,
            renderOption: renderUserSelectOption,
            getOptionLabel: (option) => option.label,
          },
        },
        {
          component: "CustomMultiSelectV2",
          props: {
            label: "Editor",
            required: false,
            selectedValues: editors,
            setSelectedValues: setEditors,
            optionsList: userOptions,
            renderOption: renderUserSelectOption,
            getOptionLabel: (option) => option.label,
          },
        },
        {
          component: "CustomMultiSelectV2",
          props: {
            label: "Viewer",
            required: false,
            selectedValues: viewers,
            setSelectedValues: setViewers,
            optionsList: userOptions,
            renderOption: renderUserSelectOption,
            getOptionLabel: (option) => option.label,
          },
        },
      ]
      : []),
  ];
};

const buildTableProjectConfigsHelper = ({
  projectDetailsSelected,
  onOpenDetailsDialog,
  projectMilestoneDetailsRows,
  handleOpenUpdateMilestoneDialog,
  tabsDataUsersPC,
}) => [
  {
    key: "zones",
    condition: projectDetailsSelected?.is_zonal,
    title: "Zones",
    count: projectDetailsSelected?.project_zones?.length || 0,
    columns: [
      { id: "zone_position", label: "Zone Position" },
      { id: "zone_name", label: "Zone Name" },
    ],
    rows:
      projectDetailsSelected?.project_zones?.map((data) => ({
        zone_position: data?.position,
        zone_name: data?.zone_name,
      })) || [],
    actionIcon: "edit",
    hideAddButton: projectDetailsSelected?.status !== PROJECT_STATUS.DRAFT,
    handleOnClick: onOpenDetailsDialog,
  },
  {
    key: "vehicleFunction",
    title: "Vehicle Function(Project)",
    count: projectDetailsSelected?.vehicle_function_details?.length || 0,
    columns: [
      { id: "vehicle_function_name", label: "Vehicle Function Name" },
      { id: "description", label: "Description" },
    ],
    rows:
      projectDetailsSelected?.vehicle_function_details?.map((data) => ({
        vehicle_function_name: data?.name,
        description: data?.other_fields?.description,
      })) || [],
    actionIcon: "edit",
    hideAddButton: projectDetailsSelected?.status !== PROJECT_STATUS.DRAFT,
    handleOnClick: onOpenDetailsDialog,
  },
  {
    key: "powerTrain",
    title: "Powertrain Class",
    count: projectDetailsSelected?.powertrain_class_details?.length || 0,
    columns: [
      { id: "powertrain_name", label: "Powertrain Name" },
      { id: "description", label: "Description" },
    ],
    rows:
      projectDetailsSelected?.powertrain_class_details?.map((data) => ({
        powertrain_name: data?.name,
        description: data?.other_fields?.description,
      })) || [],
    actionIcon: "edit",
    hideAddButton: projectDetailsSelected?.status !== PROJECT_STATUS.DRAFT,
    handleOnClick: onOpenDetailsDialog,
  },
  {
    key: "deliveryMarket",
    title: "Delivery Market",
    count: projectDetailsSelected?.delivery_market?.length || 0,
    columns: [{ id: "delivery_market", label: "Delivery Market" }],
    rows:
      projectDetailsSelected?.delivery_market?.map((data) => ({
        delivery_market: data,
      })) || [],
    actionIcon: "edit",
    hideAddButton: projectDetailsSelected?.status !== PROJECT_STATUS.DRAFT,
    handleOnClick: onOpenDetailsDialog,
  },
  {
    key: "manufacturerDetails",
    title: "Manufacturer Configurations",
    count: projectDetailsSelected?.mft_details?.length || 0,
    columns: [
      { id: "manufacturer_name", label: "Manufacturer Name" },
      { id: "address", label: "Address" },
    ],
    rows:
      projectDetailsSelected?.mft_details?.map((data) => ({
        manufacturer_name: data?.name,
        address: data?.address,
      })) || [],
    actionIcon: "edit",
    hideAddButton: projectDetailsSelected?.status !== PROJECT_STATUS.DRAFT,
    handleOnClick: onOpenDetailsDialog,
  },
  {
    key: "milestoneDetails",
    title: "Milestone Details",
    count: projectMilestoneDetailsRows.length,
    columns: [
      { id: "milestone_name", label: "Milestone Name" },
      { id: "milestone_date", label: "Milestone Date" },
    ],
    rows: projectMilestoneDetailsRows,
    actionIcon: "edit",
    hideAddButton: projectDetailsSelected?.status !== PROJECT_STATUS.DRAFT,
    handleOnClick: handleOpenUpdateMilestoneDialog,
  },
  {
    key: "users",
    title: "Users",
    onlyUsers: <CustomTabsV2 tabs={tabsDataUsersPC} />,
  },
];

function ViewProjectConfig() {
  dayjs.extend(customParseFormat);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { variantInfo } = useSelector((state) => state.variantReducer);
  const {
    projectId: stateProjectId,
    powerTrainClassData,
    adminDefaultData,
    vehicleFunctionData,
    metaData,
  } = location.state || {};
  const queryParams = new URLSearchParams(location.search);
  const projectId = stateProjectId || queryParams.get("project_id");

  const [expandPC, setExpandPC] = useState(true);
  const [expandVC, setExpandVC] = useState(false);
  const [expandPCLogs, setExpandPCLogs] = useState(false);
  const [expandVCLogs, setExpandVCLogs] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [logsProject, setLogsProject] = useState([]);
  const [logsCount, setLogsCount] = useState(0);
  const [logsCountVC, setLogsCountVC] = useState(0);
  const [logsVariant, setLogsVariant] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVariantDetails, setSelectedVariantDetails] = useState(null);
  const [openAddVariant, setOpenAddVariant] = useState(false);
  const [copyVariantSource, setCopyVariantSource] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [dialogTitleProject, setDialogTitleProject] = useState("");
  const [initialDetailsDialogValues, setInitialDetailsDialogValues] = useState([]);
  const [openRemoveConfirmDialog, setOpenRemoveConfirmDialog] = useState(false);
  const [pendingConfirmAction, setPendingConfirmAction] = useState(null);
  const [editSaveIndex, setEditSaveIndex] = useState(null);

  // Architecture edit dialog state
  const [openArchitectureDialog, setOpenArchitectureDialog] = useState(false);
  const [architectureSelected, setArchitectureSelected] = useState("");
  const [zonesSelected, setZonesSelected] = useState([]);
  const [initialArchitectureValues, setInitialArchitectureValues] = useState({
    architecture: "",
    zones: [],
  });

  // Variant Delivery Market dialog state
  const [openVariantDeliveryMarketDialog, setOpenVariantDeliveryMarketDialog] = useState(false);
  const [selectedVariantDeliveryMarket, setSelectedVariantDeliveryMarket] = useState("");
  const [editingVariantDetails, setEditingVariantDetails] = useState(null);
  const [openVariantPowertrainDialog, setOpenVariantPowertrainDialog] = useState(false);
  const [selectedVariantPowertrain, setSelectedVariantPowertrain] = useState("");
  const [openVariantManufacturerDialog, setOpenVariantManufacturerDialog] = useState(false);
  const [selectedVariantManufacturer, setSelectedVariantManufacturer] = useState("");

  const [openWorkflowDialog, setOpenWorkflowDialog] = useState(false);
  const [workflowOption, setWorkflowOption] = useState("archive");

  const [openArchiveOptionDialog, setOpenArchiveOptionDialog] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState("project");
  const [openArchiveVariantDialog, setOpenArchiveVariantDialog] = useState(false);
  const [selectedVariantsToArchive, setSelectedVariantsToArchive] = useState([]);
  const [openArchiveVariantConfirmDialog, setOpenArchiveVariantConfirmDialog] = useState(false);
  const [enableRetrievalInVlc, setEnableRetrievalInVlc] = useState(false);

  const [projectDetailsSelected, setProjectDetailsSelected] = useState(null);
  const [isRecreatedProject, setIsRecreatedProject] = useState(false);
  const [variantDetailsSelected, setVariantDetailsSelected] = useState([]);
  const [variantName, setVariantName] = useState("");
  const [modelYear, setModelYear] = useState(null);
  const [variantColor, setVariantColor] = useState("#000000");
  const [deliveryMarkets, setDeliveryMarkets] = useState(null);
  const [vehicleFunctions, setVehicleFunctions] = useState([]);
  const [powertrainClasses, setPowertrainClasses] = useState(null);
  const [manufacturerDetails, setManufacturerDetails] = useState(null);
  const [milestoneRows, setMilestoneRows] = useState([]);
  const [milestoneDraft, setMilestoneDraft] = useState(createMilestoneRow());
  const [openUpdateMilestoneDialog, setOpenUpdateMilestoneDialog] = useState(false);
  const [updateMilestoneTarget, setUpdateMilestoneTarget] = useState("project");
  const [updateMilestoneRows, setUpdateMilestoneRows] = useState([]);
  const [updateMilestoneDraft, setUpdateMilestoneDraft] = useState(
    createMilestoneRow(),
  );
  const [vehicleStatusData, setVehicleStatusData] = useState([]);
  const vehicleStatusList = resolveVehicleStatusList(vehicleStatusData);

  const [pageER, setPageER] = useState(1);
  const [rowsPerPageER, setRowsPerPageER] = useState(25);
  const [tableRowsDataER, setTableRowsDataER] = useState([]);
  const [editRequestCount, setEditRequestCount] = useState("");
  const [exportProjectDetails, setExportProjectDetails] = useState(null);
  const [exportProjectVariantsDetails, setExportProjectVariantsDetails] = useState(null);
  const handleRowPerPageER = () => {
    setRowsPerPageER((prev) => prev + 25);
    setPageER(1);
  };

  const [pagePCLogCount, setPagePCLogCount] = useState(1);
  const [rowsPerPagePCLogCount] = useState(25);

  const [pageVCLogCount, setPageVCLogCount] = useState(1);
  const [rowsPerPageVCLogCount] = useState(25);

  const [openRecreationVariant, setOpenRecreationVariant] = useState(false);
  const [recreationVariantsList, setRecreationVariantsList] = useState([]);
  const [selectedVariantForRecreation, setSelectedVariantForRecreation] = useState(null);
  const [owner, setOwner] = useState([]);
  const [coOwners, setCoOwners] = useState([]);
  const [editors, setEditors] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [usersListData, setUsersListData] = useState([]);
  const [fullUsersList, setFullUsersList] = useState([]);

  const formatDialogTitle = (text) => {
    if (!text) return "";

    return (
      text
        .replaceAll(/([a-z])([A-Z])/g, "$1 $2")
        .toLowerCase()
        .replaceAll(/\((\w)/g, (_, c) => `(${c.toUpperCase()}`)
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };
  const fetchProjectAndVariantDetails = async () => {
    if (!projectId) return;

    try {
      const [respProjectDetails, respVariantDetails] = await Promise.all([
        fetchProjectDetailsAPI(dispatch, {
          project_id: projectId,
          order_by_sort: "ASC",
        }),
        fetchVariantDetailsAPI(dispatch, {
          project_id: projectId,
          is_complete_details_required: true,
          page_number: 1,
          page_size: 10000,
          order_by_sort: "ASC",
        }),
      ]);

      const projectData = Array.isArray(respProjectDetails?.data)
        ? respProjectDetails.data[0]
        : respProjectDetails?.data || null;
      const rawVariantData = respVariantDetails?.data;
      let variantData = [];
      if (Array.isArray(rawVariantData)) {
        variantData = rawVariantData;
      } else if (Array.isArray(rawVariantData?.data)) {
        variantData = rawVariantData.data;
      }

      setProjectDetailsSelected(projectData || null);
      setVariantDetailsSelected(variantData);

      setSelectedVariantDetails((prevSelected) => {
        if (!prevSelected) return prevSelected;
        return (
          (Array.isArray(variantData) &&
            variantData.find((v) => v.variant_id === prevSelected.variant_id)) ||
          prevSelected
        );
      });

      console.log("----selectedProjectDetails--", projectData);
      console.log("---selectedVariantDetails---", variantData);
      return {
        projectData,
        variantData,
      };
    } catch (error) {
      console.error("❌ Error fetching project/variant details:", error);
    }
  };

  useEffect(() => {
    void fetchProjectAndVariantDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  console.log("projectDetailsSelected", projectDetailsSelected);
  const [selectedValuesUpdateProject, setSelectedValuesUpdateProject] = useState({
      "VEHICLE FUNCTION(PROJECT)": [],
      "VEHICLE FUNCTION(VARIANT)": [],
      "POWERTRAIN CLASS": [],
      "DELIVERY MARKET": [],
      "MANUFACTURER CONFIGURATIONS": [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    });

  useEffect(() => {
    if (projectDetailsSelected || selectedVariantDetails) {
      setIsRecreatedProject(
        Boolean(projectDetailsSelected?.parent_project_id),
      );
      setEnableRetrievalInVlc(Boolean(projectDetailsSelected?.archive_vlc));

      setSelectedValuesUpdateProject(
        buildSelectedValuesUpdateProject(
          projectDetailsSelected,
          selectedVariantDetails,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetailsSelected, selectedVariantDetails]);

  console.log("--selectedValuesUpdateProject---", selectedValuesUpdateProject);
  const getOptionsList = (selectedValues = []) => {
    const selectedIds = getSelectedValueIds(
      selectedValuesUpdateProject,
      dialogTitleProject,
    );

    const optionBuilders = {
      "VEHICLE FUNCTION(PROJECT)": () =>
        buildLabelValueOptions(
          vehicleFunctionData,
          (item) => `${item.name} (${item.abbreviation})`,
          (item) => Number.parseInt(item.id),
        ),
      "VEHICLE FUNCTION(VARIANT)": () =>
        buildLabelValueOptions(
          projectDetailsSelected?.vehicle_function_details,
          (item) => `${item.name} (${item.abbreviation})`,
          (item) => Number.parseInt(item.id),
        ),
      "POWERTRAIN CLASS": () =>
        buildLabelValueOptions(
          powerTrainClassData,
          (item) => `${item.name} (${item.abbreviation})`,
          (item) => item.id,
        ),
      "DELIVERY MARKET": () =>
        (adminDefaultData?.data?.product_delivery_markets || []).map(
          (item) => ({
            label: item,
            value: item,
          }),
        ),
      "MANUFACTURER CONFIGURATIONS": () =>
        (adminDefaultData?.data?.vehicle_manufacturers || []).map((item) => ({
          label: item?.name,
          subLabel: item?.address,
          value: item,
        })),
    };

    const buildOptions = optionBuilders[dialogTitleProject];
    const list = buildOptions ? buildOptions() : [];
    const filteredList = filterOutSelectedIds(list, selectedIds);

    return filteredList?.filter(
      (option) =>
        !selectedValues?.some((selected) => isOptionSelected(option, selected)),
    );
  };

  const handleSelectionChange = (newSelected) => {
    setSelectedValuesUpdateProject((prev) => ({
      ...prev,
      [dialogTitleProject?.toUpperCase()]: newSelected,
    }));
  };

  const radioOptions = [
    { label: "Created", value: "created" },
    { label: "Ongoing", value: "ongoing" },
    { label: "Completed", value: "completed" },
  ];

  const milestoneNameOptions = getMilestoneNameOptions(
    projectDetailsSelected?.milestones,
    vehicleStatusList,
  );


  const componentMap = {
    CustomFormTextField,
    CustomSelectV2,
    CustomSelectV3,
    CustomMultiSelectV2,
    CustomRadioButtonV2,
    CustomDatePicker,
    CustomSelectVariantColor,
    CustomDatePickerVariant,
    CustomFormTextFieldV3,
    MilestoneSection,
  };

  const getMilestoneSectionProps = ({
    rows,
    draft,
    setRows,
    setDraft,
  }) => ({
    rows,
    draft,
    milestoneNameOptions,
    onAddMilestone: createAddMilestoneHandler(
      setRows,
      setDraft,
      draft,
      milestoneNameOptions,
    ),
    onRemoveMilestone: createRemoveMilestoneHandler(setRows),
  });

  const setFormStates = (selectedVariant, options = {}) => {
    const { clearVariantName = false } = options;
    const parentModelYear = dayjs(selectedVariant?.production_year, "MM-YYYY");

    const parentVehicleFunctionDetails =
      selectedVariant?.vehicle_function_details?.map((item) => ({
        label: `${item.name} (${item.abbreviation})`,
        value: item?.id,
      })) || [];

    const parentCoOwnerDetails = mapUserDetailsForForm(selectedVariant?.co_owner_details);

    const parentEditUserDetails = mapUserDetailsForForm(selectedVariant?.edit_user_details);

    const parentViewUserDetails = mapUserDetailsForForm(selectedVariant?.view_user_details);

    const deliveryMarket = getFirstItem(selectedVariant?.delivery_market);
    const powertrainClass =
      selectedVariant?.pwrtrn_class ||
      selectedVariant?.powertrain_class_details?.[0]?.id;
    const selectedManufacturerDetails = getFirstItem(selectedVariant?.mft_details);

    setVariantName(clearVariantName ? "" : selectedVariant?.name || "");
    setModelYear(parentModelYear || null);
    setVariantColor(selectedVariant?.color_code || "#000000");
    setDeliveryMarkets(deliveryMarket || null);
    setVehicleFunctions(parentVehicleFunctionDetails || []);
    setPowertrainClasses(Number(powertrainClass) || null);
    setCoOwners(parentCoOwnerDetails || []);
    setEditors(parentEditUserDetails || []);
    setViewers(parentViewUserDetails || []);
    setMilestoneRows(
      mapProjectMilestonesToRows(
        selectedVariant?.milestones || projectDetailsSelected?.milestones,
      ),
    );
    setMilestoneDraft(createMilestoneRow());

    setManufacturerDetails(
      findMatchedManufacturer(
        projectDetailsSelected?.mft_details,
        selectedManufacturerDetails,
      ),
    );
  };

  const formLayout = buildVariantFormLayout({
    openRecreationVariant,
    selectedVariantForRecreation,
    setSelectedVariantForRecreation,
    setFormStates,
    recreationVariantsList,
    variantName,
    setVariantName,
    openAddVariant,
    milestoneSectionProps: getMilestoneSectionProps({
      rows: milestoneRows,
      draft: milestoneDraft,
      setRows: setMilestoneRows,
      setDraft: setMilestoneDraft,
    }),
    modelYear,
    setModelYear,
    variantColor,
    setVariantColor,
    deliveryMarkets,
    setDeliveryMarkets,
    projectDetailsSelected,
    vehicleFunctions,
    setVehicleFunctions,
    powertrainClasses,
    setPowertrainClasses,
    manufacturerDetails,
    setManufacturerDetails,
    coOwners,
    setCoOwners,
    editors,
    setEditors,
    viewers,
    setViewers,
    usersListData,
  });

  const singleColumnRows = [1, 2, 4, 5, 6, 7, 9];

  const updateMilestoneFormLayout = [
    {
      component: "MilestoneSection",
      props: getMilestoneSectionProps({
        rows: updateMilestoneRows,
        draft: updateMilestoneDraft,
        setRows: setUpdateMilestoneRows,
        setDraft: setUpdateMilestoneDraft,
      }),
    },
  ];

  const handleExpandPC = (value) => {
    setExpandPC(value);
    setExpandVC(!value);
    handleVariantToggle(0, variantDetailsSelected?.[0]);
  };

  const handleExpandVC = (value) => {
    setExpandVC(value);
    if (value) {
      setExpandPC(false);
      handleVariantToggle(0, variantDetailsSelected?.[0]);
    } else {
      setExpandPC(true);
    }
  };

  const handleBackToPC = () => {
    setExpandPC(true);
    setExpandVC(false);
  };

  const handleExpandPCLogs = (value) => {
    setExpandPCLogs(value);
  };
  const handleExpandVCLogs = (value) => {
    setExpandVCLogs(value);
  };

  const handleVariantToggle = (index, variant) => {
    setExpandedIndex((prev) => {
      const newIndex = prev === index ? null : index;

      if (newIndex === null) {
        setExpandPC(true);
        setExpandVC(false);
      } else {
        setExpandPC(false);
      }

      return newIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    setSelectedVariantDetails(variant);
  };

  const handleAddVariant = (e) => {
    e.stopPropagation();
    setCopyVariantSource(null);
    setOpenAddVariant(true);
  };

  const handleOpenCloseAddVariant = (value) => {
    setOpenAddVariant(value);
    setOpenRecreationVariant(value);
    if (!value) {
      setVariantName("");
      setModelYear(null);
      setVariantColor("#000000");
      setDeliveryMarkets(null);
      setVehicleFunctions([]);
      setPowertrainClasses(null);
      setManufacturerDetails(null);
      setMilestoneRows([]);
      setMilestoneDraft(createMilestoneRow());
      setCopyVariantSource(null);
    }
  };

  const handleOpenUpdateMilestoneDialog = (target = "project") => {
    const milestoneSource =
      target === "variant"
        ? selectedVariantDetails?.milestones || projectDetailsSelected?.milestones
        : projectDetailsSelected?.milestones;

    setUpdateMilestoneTarget(target);
    console.log("---milestoneSource---", milestoneSource)

    if (!milestoneSource || milestoneSource.length === 0) {
      setUpdateMilestoneRows([]);
    } else {
      setUpdateMilestoneRows(
        mapProjectMilestonesToRows(milestoneSource),
      );
    }
    
    setUpdateMilestoneDraft(createMilestoneRow());
    setOpenUpdateMilestoneDialog(true);
  };

  const handleCloseUpdateMilestoneDialog = () => {
    setOpenUpdateMilestoneDialog(false);
    setUpdateMilestoneTarget("project");
    setUpdateMilestoneRows([]);
    setUpdateMilestoneDraft(createMilestoneRow());
  };

  const fetchInitialData = async () => {
    try {
      const usersList = await Promise.resolve(dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_users_list,
            params: {
              page_size: 1000,
              page_number: 1,
            },
          })
        ));

      setUsersListData(usersList?.data || []);
      setFullUsersList(usersList?.data || []);
    } catch (error) {
      console.error("❌ Failed to load initial data:", error);
    }
  };

  useEffect(() => {
    if (openRecreationVariant) {
      void fetchInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRecreationVariant]);

  useEffect(() => {
    if (openAddVariant && !openRecreationVariant) {
      setMilestoneRows(
        mapProjectMilestonesToRows(
          copyVariantSource?.milestones || projectDetailsSelected?.milestones,
        ),
      );
      setMilestoneDraft(createMilestoneRow());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    copyVariantSource,
    openAddVariant,
    openRecreationVariant,
    projectDetailsSelected?.milestones,
  ]);

  useEffect(() => {
    if (
      milestoneDraft.name &&
      milestoneRows.some((milestone) => milestone.name === milestoneDraft.name)
    ) {
      setMilestoneDraft((prev) => ({
        ...prev,
        name: "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestoneDraft.name, milestoneRows]);

  useEffect(() => {
    console.log("---form users---", coOwners, editors, viewers);
    console.log("---userData---", fullUsersList);

    if (!Array.isArray(fullUsersList)) return;

    const selectedUsers = new Set([...coOwners, ...editors, ...viewers, ...owner].map(
      (user) => user.value,
    ));

    const unSelectedUserListData = fullUsersList.filter(
      (user) => !selectedUsers.has(user.user_id),
    );

    setUsersListData(unSelectedUserListData);

    console.log("---unSelectedUserListData---", unSelectedUserListData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coOwners, editors, viewers, fullUsersList]);

  const handleCopyVariant = (variant, event) => {
    event?.stopPropagation();
    setOpenRecreationVariant(false);
    setSelectedVariantDetails(variant);
    setCopyVariantSource(variant);
    setFormStates(variant, { clearVariantName: true });
    setOpenAddVariant(true);
  };

  const handleRecreateVariantClick = async () => {
    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...projectConfigAPI.get_variants_for_recreation,
        params: {
          project_id: projectDetailsSelected.project_id,
          parent_project_id: projectDetailsSelected.parent_project_id,
        },
      }),
    ));
    if (resp.error) {
      showToastMessage({
        type: "error",
        message: resp.msg,
        // eslint-disable-next-line react-hooks/exhaustive-deps
      });
    } else {
      setRecreationVariantsList(resp?.data);

      setSelectedVariantForRecreation(resp?.data?.[0]);

      setFormStates(resp?.data?.[0]);
      setOpenRecreationVariant(true);
    }
  };

  const handleCloseRecreateVariant = (value) => {
    setOpenRecreationVariant(value);
  };

  const handleClickRecreateDialogVariant = ({ draft = false }) => {
    let reqObj = {
      project_id: Number.parseInt(projectDetailsSelected?.project_id),
      name: variantName,
      color_code: variantColor,
      variant_status: selectedVariantForRecreation?.status,
      save_as_draft: draft,
      delivery_market: deliveryMarkets,
      pwrtrn_class: powertrainClasses,
      ...(vehicleFunctions &&
        vehicleFunctions?.length > 0 && {
        veh_funcs: vehicleFunctions?.map((data) => data?.value),
      }),
      production_year: modelYear?.format("MM-YYYY"),
      current_time: moment().format(),
      mft_details: manufacturerDetails,
      milestones: buildMilestonesPayload(),
      parent_variant_id: selectedVariantForRecreation?.variant_id,
      parent_project_id: projectDetailsSelected.parent_project_id,
      ...(coOwners?.length > 0
        ? {
          co_owners: coOwners?.map((data) => data?.value),
        }
        : null),
      ...(editors?.length > 0
        ? {
          edit_users: editors?.map((data) => data?.value),
        }
        : null),
      ...(viewers?.length > 0
        ? {
          view_users: viewers?.map((data) => data?.value),
        }
        : null),
    };
    console.log("VarisntreqObj", reqObj);

    dispatch(
      HandleApiActions({
        ...projectConfigAPI.add_recreated_variant,
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      void fetchProjectAndVariantDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    handleOpenCloseAddVariant(false);
  };

  const buildMilestonesPayload = (rows = milestoneRows) =>
    rows
      .slice()
      .sort((a, b) => compareMilestonesBySequence(a, b, milestoneNameOptions))
      .map((milestone) => ({
        name: milestone.name?.trim(),
        milestone_date: milestone.milestoneDate?.format("MM-YYYY"),
        status: true,
      }))
      .filter((milestone) => milestone.name && milestone.milestone_date);

  const normalizeMilestonesForCompare = (milestones = []) =>
    milestones
      .map((milestone) => ({
        name: milestone?.name?.trim(),
        milestone_date:
          parseMilestoneDate(milestone?.milestone_date)?.format("MM-YYYY") ||
          milestone?.milestone_date,
        status: milestone?.status ?? true,
      }))
      .filter((milestone) => milestone.name && milestone.milestone_date)
      .sort((a, b) => compareMilestonesBySequence(a, b, milestoneNameOptions));

  const originalMilestonePayload = normalizeMilestonesForCompare(
    resolveOriginalMilestones(
      updateMilestoneTarget,
      selectedVariantDetails,
      projectDetailsSelected,
    ),
  );
  const updateMilestonePayload = buildMilestonesPayload(updateMilestoneRows);
  const hasMilestoneChanges =
    JSON.stringify(originalMilestonePayload) !==
    JSON.stringify(updateMilestonePayload);

  const hasInvalidMilestones =
    (openAddVariant || openRecreationVariant) &&
    (milestoneRows.length !== milestoneNameOptions.length ||
      milestoneRows.some(
        (milestone) =>
          milestone.name?.trim().length < 3 || !milestone.milestoneDate,
      ));

  const isAddDisabledVC =
    variantName.trim().length < 3 ||
    !vehicleFunctions?.length ||
    !modelYear ||
    !deliveryMarkets ||
    !powertrainClasses ||
    !manufacturerDetails ||
    hasInvalidMilestones;

  const isUpdateMilestoneDisabled =
    updateMilestoneRows.length !== milestoneNameOptions.length ||
    updateMilestoneRows.some(
      (milestone) =>
        milestone.name?.trim().length < 3 || !milestone.milestoneDate,
    ) ||
    !hasMilestoneChanges;

  const handleUpdateMilestones = async () => {
    const currentVariantId = selectedVariantDetails?.variant_id;
    const reqObj = {
      project_id: Number.parseInt(projectDetailsSelected?.project_id),
      ...(updateMilestoneTarget === "variant" && {
        variant_id: Number.parseInt(currentVariantId),
      }),
      current_time: moment().toISOString(),
      milestones: buildMilestonesPayload(updateMilestoneRows),
    };

    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...(updateMilestoneTarget === "variant"
          ? projectConfigAPI.update_variant_milestones
          : projectConfigAPI.update_project_milestones),
        params: reqObj,
        show_toast: true,
      }),
    ));

    if (!resp?.error) {
      const projectVariantResp = await fetchProjectAndVariantDetails();

      if (updateMilestoneTarget === "variant") {
        const variantList = Array.isArray(projectVariantResp?.variantData)
          ? projectVariantResp.variantData
          : [];
        const updatedVariant =
          variantList.find(
            (variant) => variant.variant_id === currentVariantId,
          ) || null;

        setSelectedVariantDetails(
          updatedVariant || {
            ...selectedVariantDetails,
            milestones: reqObj.milestones,
          },
        );
      }

      handleCloseUpdateMilestoneDialog();
    }
  };

  const handleProjectLogs = async ({ project_id, page_number, page_size }) => {
    try {
      const queryParams = { project_id, page_number, page_size };
      const resp = await Promise.resolve(dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_project_change_logs,
          params: queryParams,
        }),
      ));
      return resp;
    } catch (error) {
      console.error("❌ Error fetching project logs:", error.message);
      throw error;
    }
  };

  const handleVariantLogs = async ({
    project_id,
    page_number,
    page_size,
    variant_id,
  }) => {
    try {
      const queryParams = { project_id, page_number, page_size, variant_id };
      const resp = await Promise.resolve(dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_variant_change_logs,
          params: queryParams,
        }),
      ));
      return resp;
    } catch (error) {
      console.error("❌ Error fetching project logs:", error.message);
      throw error;
    }
  };

  const fetchLogsProject = async () => {
    setLoading(true);
    try {
      const response = await handleProjectLogs({
        project_id: projectDetailsSelected?.project_id,
        page_number: pagePCLogCount,
        page_size: rowsPerPagePCLogCount,
    // eslint-disable-next-line react-hooks/exhaustive-deps
      });
      console.log("---response---response", response);

      setLogsProject(response?.data || []);
      setLogsCount(response?.total_count);
    } catch (error) {
      console.error("Failed to load logs:", error);
    } finally {
      setLoading(false);
    }
  };
  console.log("logsCount", logsCount);

  const handlePageChangeLogs = async (newPage) => {
    setPagePCLogCount(newPage);
  };
  const handlePageChangeLogsVC = async (newPage) => {
    setPageVCLogCount(newPage);
  };

  const fetchLogsVariant = async () => {
    setLoading(true);
    try {
      const response = await handleVariantLogs({
        project_id: projectDetailsSelected?.project_id,
        variant_id: selectedVariantDetails?.variant_id,
        page_number: pageVCLogCount,
        page_size: rowsPerPageVCLogCount,
    // eslint-disable-next-line react-hooks/exhaustive-deps
      });
      console.log("---response---response", response);

      setLogsVariant(response?.data || []);
      console.log("--Variant Logs REsponse", response?.total_count);
      setLogsCountVC(response?.total_count);
    } catch (error) {
      console.error("Failed to load logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleStatusSettings = async ({ page_number, page_size }) => {
    try {
      const resp = await Promise.resolve(dispatch(
        HandleApiActions({
          ...adminSettingsAPI?.get_vehicle_status,
          params: { page_number, page_size },
        }),
      ));
      console.log("--Vehicle Status Resp---", resp);
      setVehicleStatusData(Array.isArray(resp?.data) ? resp.data : []);

      return resp;
    } catch (error) {
      console.error("❌ Failed to load vehicle status:", error);
    }
  };
  useEffect(() => {
    void fetchLogsProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePCLogCount, rowsPerPagePCLogCount]);

  useEffect(() => {
    void fetchLogsVariant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageVCLogCount, rowsPerPageVCLogCount]);

  useEffect(() => {
    void fetchVehicleStatusSettings({ page_number: 1, page_size: 1000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetailsSelected, variantDetailsSelected]);

  useEffect(() => {
    setOwner(selectedVariantForRecreation?.owner_details?.[0]?.user_id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariantForRecreation]);

  useEffect(() => {
    if (projectDetailsSelected) {
      void fetchLogsProject();
    }

    if (selectedVariantDetails) {
      void fetchLogsVariant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetailsSelected, selectedVariantDetails]);

  const tabsDataUsersPC = buildTabsDataUsersPC(projectDetailsSelected);


  const tabsDataUsersVC = buildTabsDataUsersVC(selectedVariantDetails);
  const projectDetails = [
    {
      label: "Project Status",
      value: Object.keys(metaData?.project_status || {}).find(
        (key) =>
          metaData?.project_status?.[key] === projectDetailsSelected?.status,
      ),
      editable: true,
      optionList: buildProjectStatusOptionList(
        metaData?.project_status,
        Boolean(
          projectDetailsSelected?.is_certificate_uploaded &&
            projectDetailsSelected?.is_certificate_uploaded !== "false",
        ),
      ),
    },
    { label: "Architecture", value: projectDetailsSelected?.architecture_name, editable: projectDetailsSelected?.status === PROJECT_STATUS.DRAFT, openDialog: true },
    {
      label: "Trademark",
      value: projectDetailsSelected?.trade_mark,
      editable: projectDetailsSelected?.status === PROJECT_STATUS.DRAFT,
    },
    {
      label: "Vehicle Platform",
      value: projectDetailsSelected?.vehicle_platform,
    },
    {
      label: "Vehicle Class",
      value: `${projectDetailsSelected?.vehicle_class_details?.[0]?.class || ""
        } - ${projectDetailsSelected?.vehicle_class_details?.[0]?.sub_class || ""
        }`,
    },
    {
      label: "Vehicle Name",
      value: `${projectDetailsSelected?.vehicle_class_details?.[0]?.name || ""
        }`,
    },
    { label: "No of Seats", value: projectDetailsSelected?.no_of_seats },
    { label: "Target Market", value: projectDetailsSelected?.target_market },
    {
      label: "Created At",
      value: dayjs(projectDetailsSelected?.created_at).format(
        "DD MMM YYYY, hh:mm A",
      ),
    },
    { label: "Model Year", value: projectDetailsSelected?.model_year },
  ];

  const projectMilestoneDetailsRows = buildMilestoneDetailsRows(
    projectDetailsSelected?.milestones,
    vehicleStatusList,
  );

  const handleCloseConfigDetailsDialog = () => {
    if (dialogTitleProject && initialDetailsDialogValues) {
      setSelectedValuesUpdateProject((prev) => ({
        ...prev,
        [dialogTitleProject]: initialDetailsDialogValues,
      }));
    }
    setOpenDetailsDialog(false);
  };

  const handleOpenConfigDetailsDialog = (title) => {
    const key = title?.toUpperCase();
    console.log("Clicked:", title);
    const freshValuesMap = buildSelectedValuesUpdateProject(
      projectDetailsSelected,
      selectedVariantDetails,
    );
    const currentSavedValues = freshValuesMap[key] || [];
    setSelectedValuesUpdateProject((prev) => ({
      ...prev,
      [key]: currentSavedValues,
    }));
    setDialogTitleProject(key);
    setInitialDetailsDialogValues(currentSavedValues);
    setOpenDetailsDialog(true);
  };

  const tableProjectConfigs = buildTableProjectConfigsHelper({
    projectDetailsSelected,
    onOpenDetailsDialog: handleOpenConfigDetailsDialog,
    projectMilestoneDetailsRows,
    handleOpenUpdateMilestoneDialog,
    tabsDataUsersPC,
  });

  const isVehicleFunctionVariant = {
    title: "Vehicle Function(Variant)",
    count: selectedVariantDetails?.vehicle_function_details?.length || 0,
    columns: [
      { id: "vehicle_function_name", label: "Vehicle Function Name" },
      { id: "description", label: "Description" },
    ],
    rows:
      selectedVariantDetails?.vehicle_function_details?.map((data) => ({
        vehicle_function_name: data?.name,
        description: data?.other_fields?.description,
      })) || [],
    actionIcon: "edit",
    hideAddButton: projectDetailsSelected?.status !== PROJECT_STATUS.DRAFT,
    handleOnClick: handleOpenConfigDetailsDialog,
  };
  const variantMilestoneDetailsRows = buildMilestoneDetailsRows(
    selectedVariantDetails?.milestones || projectDetailsSelected?.milestones,
    vehicleStatusList,
  );
  const isMilestoneDetailsVariant = {
    title: "Milestone Details",
    count: variantMilestoneDetailsRows.length,
    columns: [
      { id: "milestone_name", label: "Milestone Name" },
      { id: "milestone_date", label: "Milestone Date" },
    ],
    rows: variantMilestoneDetailsRows,
    actionIcon: "edit",
    hideAddButton: projectDetailsSelected?.status !== PROJECT_STATUS.DRAFT,
    handleOnClick: () => handleOpenUpdateMilestoneDialog("variant"),
  };
  const isVariantUsers = {
    key: "users",
    title: "Users",
    onlyUsers: <CustomTabsV2 tabs={tabsDataUsersVC} />,
  };

  const isVariantZones = {
    title: "Zones",

    count: selectedVariantDetails?.zone_details?.length || 0,
    columns: [
      { id: "zone_position", label: "Zone Position" },
      { id: "zone_name", label: "Zone Name" },
    ],
    rows:
      selectedVariantDetails?.zone_details?.map((data, idx) => ({
        zone_position: data?.zone?.position,
        zone_name: data?.zone?.zone_name,
      })) || [],
    actionIcon: "edit",
    hideAddButton: projectDetailsSelected?.status !== PROJECT_STATUS.DRAFT,
    handleOnClick: console.log("Hie"),
  };

  const handleUpdateProjectStatus = async (editedValue) => {
    const reqObj = {
      project_id: Number.parseInt(projectDetailsSelected?.project_id),
      status: editedValue,
      current_time: moment().format(),
    };

    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...projectConfigAPI.update_project_status,
        params: reqObj,
        show_toast: true,
      }),
    ));
    if (!resp.error) {
      await fetchProjectDetailsAPI(dispatch, {
        project_id: projectDetailsSelected?.project_id,
      });

      setProjectDetailsSelected((prev) => ({
        ...prev,
        status: editedValue,
      }));

      setEditSaveIndex(true);
    }
  };

  const handleUpdateVariantColor = async (variant, editedValue) => {
    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...projectConfigAPI.update_variant_color,
        params: {
          project_id: variant?.project_id,
          variant_id: variant?.variant_id,
          color_code: editedValue,
        },
        show_toast: true,
      }),
    ));
    if (!resp?.error) {
      await fetchVariantDetailsAPI(dispatch, {
        project_id: variant?.project_id,
        is_complete_details_required: true,
      });

      setVariantDetailsSelected((prev) =>
        prev.map((item) =>
          item?.variant_id === variant?.variant_id
            ? { ...item, color_code: editedValue }
            : item,
        ),
      );
    }
  };

  const handleUpdateVehicleStatus = async (variant, editedValue) => {
    const reqObj = {
      project_id: variant?.project_id,
      variant_id: variant?.variant_id,
      vehicle_status: editedValue,
      reason: "want changes",
    };
    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...projectConfigAPI?.update_vehicle_status,
        params: reqObj,
        show_toast: true,
      }),
    ));
    if (!resp?.error) {
      await fetchProjectAndVariantDetails();
    }
  };

  const handleEditClick = async (label, editedValue, variant) => {
    const upper = label?.toUpperCase();
    console.log("Edit clicked for:", upper);
    console.log("editedValueeditedValue", editedValue);

    if (upper === "ARCHITECTURE") {
      const archName = projectDetailsSelected?.architecture_name || "";
      const zones = (projectDetailsSelected?.project_zones || []).map((z) => ({
        label: z?.zone_name,
        value: z?.position,
      }));
      setArchitectureSelected(archName);
      setZonesSelected(zones);
      setInitialArchitectureValues({
        architecture: archName,
        zones,
      });
      setOpenArchitectureDialog(true);
      return;
    }

    if (upper === "DELIVERY MARKET") {
      setEditingVariantDetails(variant);
      setSelectedVariantDeliveryMarket(editedValue || "");
      setOpenVariantDeliveryMarketDialog(true);
      return;
    }

    if (upper === "POWER TRAIN CLASS") {
      setEditingVariantDetails(variant);
      setSelectedVariantPowertrain(variant?.powertrain_class_details?.[0]?.id || "");
      setOpenVariantPowertrainDialog(true);
      return;
    }

    if (upper === "MANUFACTURE NAME") {
      setEditingVariantDetails(variant);
      setSelectedVariantManufacturer(variant?.mft_details?.name || "");
      setOpenVariantManufacturerDialog(true);
      return;
    }

    if (upper === "TRADEMARK") {
      void handleClickEdit(upper, editedValue);
      return;
    }

    if (upper === "PROJECT STATUS") {
      await handleUpdateProjectStatus(editedValue);
      return;
    }

    if (upper === "VARIANT COLOR") {
      await handleUpdateVariantColor(variant, editedValue);
      return;
    }

    if (upper === "VEHICLE STATUS") {
      await handleUpdateVehicleStatus(variant, editedValue);
    }
  };

  console.log("----VariantDetailsSelected-=", variantDetailsSelected);

  const fetchEditRequests = async ({
    project_id,
    variant_id,
    er_id,
    status,
    is_sent_requests,
    ee_id,
    swm_id,
    page_number,
    page_size,
  }) => {
    const apiDetails = editRequestAPI.get_edit_requests;

    if (!project_id || !variant_id || !page_number || !page_size) {
      console.error("❌ Missing mandatory params in fetchEditRequests:", {
        project_id,
        variant_id,
        page_number,
        page_size,
    // eslint-disable-next-line react-hooks/exhaustive-deps
      });
      return;
    }

    const params = {
      project_id,
      variant_id,
      page_number,
      page_size,
      ...(er_id && { er_id }),
      ...(status && { status }),
      ...(is_sent_requests && { is_sent_requests }),
      ...(ee_id && { ee_id }),
      ...(swm_id && { swm_id }),
    };

    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params,
      }),
    ));

    console.log("---Fetch Edit Requests Resp---", resp);
    setEditRequestCount(resp?.total_count > 0 ? resp.total_count : "");
    const mappedRows = resp?.data?.length > 0 ? mapEditRequestRows(resp.data) : [];
    setTableRowsDataER(mappedRows);
    if (resp?.data?.length > 0) return resp;
  };

  useEffect(() => {
    void fetchEditRequests({
      project_id: projectDetailsSelected?.project_id,
      variant_id: selectedVariantDetails?.variant_id,
      page_number: pageER,
      page_size: rowsPerPageER,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageER, rowsPerPageER, projectDetailsSelected, selectedVariantDetails]);

  const tabsDataProject = [
    {
      name: "Details",
      count: "",
      content: (
        <ViewPCDetails
          selectedProjectDetails={projectDetailsSelected}
          projectDetails={projectDetails}
          handleEditClick={handleEditClick}
          editSaveIndex={editSaveIndex}
        />
      ),
    },
    {
      name: "Logs",
      count: logsCount || "",
      content: (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ShowLogsDetails
            page={pagePCLogCount}
            rowsPerPage={rowsPerPagePCLogCount}
            logsCount={logsCount}
            expanded={expandPCLogs}
            setExpanded={handleExpandPCLogs}
            logs={logsProject}
            loading={loading}
            onPageChange={handlePageChangeLogs}
          />
        </LocalizationProvider>
      ),
    },
  ];

  const tabsData1 = [
    {
      name: "Details",
      count: "",
      content: (
        <ShowVariantDetails
          projectDetailsSelected={projectDetailsSelected}
          isProjectZones={isVariantZones}
          isVehicleFunction={isVehicleFunctionVariant}
          isMilestoneDetails={isMilestoneDetailsVariant}
          isVariantUsers={isVariantUsers}
        />
      ),
    },
    {
      name: "Requests",
      count: editRequestCount,
      content: (
        <ShowVarientRequestDetails
          handleRowPerPageER={handleRowPerPageER}
          editRequestCount={editRequestCount}
          fetchEditRequests={fetchEditRequests}
          page={pageER}
          rowsPerPage={rowsPerPageER}
          tableRowsData={tableRowsDataER}
        />
      ),
    },
  ];

  const handleClickAddDialogVariant = () => {
    let reqObj = {
      project_id: Number.parseInt(projectDetailsSelected?.project_id),
      name: variantName,
      color_code: variantColor,
      delivery_market: deliveryMarkets,
      pwrtrn_class: powertrainClasses,
      ...(vehicleFunctions &&
        vehicleFunctions?.length > 0 && {
        veh_funcs: vehicleFunctions?.map((data) => data?.value),
      }),
      production_year: modelYear?.format("MM-YYYY"),
      current_time: moment().format(),
      mft_details: manufacturerDetails,
      milestones: buildMilestonesPayload(),
    };
    console.log("VarisntreqObj", reqObj);

    dispatch(
      HandleApiActions({
        ...projectConfigAPI.add_variant,
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      void fetchProjectAndVariantDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    handleOpenCloseAddVariant(false);
  };

  const handleConfirmArchitectureSubmit = () => {
    const isZonal = architectureSelected?.toUpperCase() === "ZONAL";
    const wasZonal = (initialArchitectureValues.architecture || "").toUpperCase() === "ZONAL";

    // If architecture changed away from ZONAL and had previous zones, or if any previous zone was removed
    const hasRemovedZones =
      (wasZonal && !isZonal && (initialArchitectureValues.zones || []).length > 0) ||
      (wasZonal &&
        isZonal &&
        (initialArchitectureValues.zones || []).some(
          (initialZone) =>
            !(zonesSelected || []).some(
              (currZone) => currZone?.value === initialZone?.value,
            ),
        ));

    if (hasRemovedZones) {
      setPendingConfirmAction("architecture");
      setOpenRemoveConfirmDialog(true);
      return;
    }
    void handleSaveArchitecture();
  };

  const handleSaveArchitecture = async () => {
    const isZonal = architectureSelected?.toUpperCase() === "ZONAL";
    const reqObj = {
      project_id: Number.parseInt(projectDetailsSelected?.project_id),
      architecture_name: architectureSelected?.toUpperCase(),
      current_time: moment().format(),
      ...(isZonal && zonesSelected?.length > 0
        ? {
          zones: zonesSelected.map((z) => ({
            zone_name: z?.label,
            position: z?.value,
          })),
        }
        : {}),
    };

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...projectConfigAPI.update_project,
          params: reqObj,
          show_toast: true,
        })
      )
    );

    if (!resp?.error) {
      setProjectDetailsSelected((prev) => ({
        ...prev,
        architecture_name: architectureSelected?.toUpperCase(),
        ...(isZonal
          ? {
            project_zones: zonesSelected.map((z) => ({
              zone_name: z?.label,
              position: z?.value,
            })),
          }
          : { project_zones: [] }),
      }));
      setOpenArchitectureDialog(false);
    }
  };

  const handleToggleEnableRetrievalInVlc = async (val) => {
    const targetProjectId = Number.parseInt(
      projectDetailsSelected?.project_id || projectId,
      10,
    );
    if (!targetProjectId) return;

    setEnableRetrievalInVlc(val);

    const reqObj = {
      project_id: targetProjectId,
      archive_vlc: val,
      current_time: moment().toISOString(),
    };

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...projectConfigAPI.update_project,
          params: reqObj,
          show_toast: true,
        }),
      ),
    );

    if (resp?.error) {
      setEnableRetrievalInVlc(Boolean(projectDetailsSelected?.archive_vlc));
    } else {
      setProjectDetailsSelected((prev) =>
        prev ? { ...prev, archive_vlc: val } : null,
      );
      await fetchProjectAndVariantDetails();
      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_projects_main,
            show_toast: false,
            params: {
              page_number: 1,
              page_size: 10000,
              order_by_column: "created_at",
              order_by_sort: "DESC",
            },
          }),
        ),
      );
    }
  };

  const handleSaveVariantDeliveryMarket = async () => {
    const reqObj = {
      project_id: Number.parseInt(editingVariantDetails?.project_id, 10),
      variant_id: Number.parseInt(editingVariantDetails?.variant_id, 10),
      delivery_market: selectedVariantDeliveryMarket,
    };

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...projectConfigAPI.update_variant,
          params: reqObj,
          show_toast: true,
        })
      )
    );
    if (!resp?.error) {
      await fetchProjectAndVariantDetails();
      setVariantDetailsSelected((prev) =>
        prev.map((item) =>
          item?.variant_id === editingVariantDetails?.variant_id
            ? { ...item, delivery_market: selectedVariantDeliveryMarket }
            : item
        )
      );
      setOpenVariantDeliveryMarketDialog(false);
    }
  };

  const handleSaveVariantPowertrain = async () => {
    const reqObj = {
      project_id: Number.parseInt(editingVariantDetails?.project_id, 10),
      variant_id: Number.parseInt(editingVariantDetails?.variant_id, 10),
      pwrtrn_class: Number.parseInt(selectedVariantPowertrain, 10),
    };

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...projectConfigAPI.update_variant,
          params: reqObj,
          show_toast: true,
        })
      )
    );
    if (!resp?.error) {
      await fetchProjectAndVariantDetails();
      setOpenVariantPowertrainDialog(false);
    }
  };

  const handleSaveVariantManufacturer = async () => {
    const mft = projectDetailsSelected?.mft_details?.find(m => m.name === selectedVariantManufacturer);
    const reqObj = {
      project_id: Number.parseInt(editingVariantDetails?.project_id, 10),
      variant_id: Number.parseInt(editingVariantDetails?.variant_id, 10),
      mft_details: mft ? { name: mft.name, address: mft.address } : null,
    };

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...projectConfigAPI.update_variant,
          params: reqObj,
          show_toast: true,
        })
      )
    );
    if (!resp?.error) {
      await fetchProjectAndVariantDetails();
      setOpenVariantManufacturerDialog(false);
    }
  };

  const handleConfirmVariantDeliveryMarketSubmit = () => {
    const initialMarket = editingVariantDetails?.delivery_market || "";
    if (selectedVariantDeliveryMarket && selectedVariantDeliveryMarket !== initialMarket) {
      setPendingConfirmAction("variant_delivery_market");
      setOpenRemoveConfirmDialog(true);
      return;
    }
    void handleSaveVariantDeliveryMarket();
  };

  const handleConfirmVariantPowertrainSubmit = () => {
    const initialPowertrain = editingVariantDetails?.powertrain_class_details?.[0]?.id ?? "";
    if (
      selectedVariantPowertrain &&
      String(selectedVariantPowertrain) !== String(initialPowertrain)
    ) {
      setPendingConfirmAction("variant_powertrain");
      setOpenRemoveConfirmDialog(true);
      return;
    }
    void handleSaveVariantPowertrain();
  };

  const handleConfirmVariantManufacturerSubmit = () => {
    const initialMft = editingVariantDetails?.mft_details?.name || "";
    if (
      selectedVariantManufacturer &&
      selectedVariantManufacturer !== initialMft
    ) {
      setPendingConfirmAction("variant_manufacturer");
      setOpenRemoveConfirmDialog(true);
      return;
    }
    void handleSaveVariantManufacturer();
  };

  const handleConfirmDetailsSubmit = () => {
    const key = dialogTitleProject?.toUpperCase();
    const currentValues = selectedValuesUpdateProject[key] || [];
    const hasRemovedExisting = (initialDetailsDialogValues || []).some(
      (initialItem) => !currentValues.some((currItem) => isOptionSelected(initialItem, currItem)),
    );
    if (hasRemovedExisting) {
      setPendingConfirmAction("details");
      setOpenRemoveConfirmDialog(true);
      return;
    }
    void handleClickEdit(key);
  };

  const handleClickEdit = async (title, editedValue) => {
    try {
      console.log("Edit Title Update Project and Variant:", title, editedValue);

      const isProjectUpdate =
        title === "TRADEMARK" ||
        title === "VEHICLE FUNCTION(PROJECT)" ||
        title === "POWERTRAIN CLASS" ||
        title === "MANUFACTURER CONFIGURATIONS" ||
        title === "DELIVERY MARKET";

      const isVariantUpdate = title === "VEHICLE FUNCTION(VARIANT)";

      if (isProjectUpdate) {
        const reqObj = buildProjectUpdateReqObj(
          projectDetailsSelected?.project_id,
          title,
          editedValue,
          selectedValuesUpdateProject,
        );

        console.log("📤 Project Update Request:", reqObj);

        const resp = await Promise.resolve(dispatch(
          HandleApiActions({
            ...projectConfigAPI.update_project,
            params: reqObj,
            show_toast: true,
          }),
        ));

        if (!resp?.error) {
          await fetchProjectAndVariantDetails();
          setProjectDetailsSelected((prev) =>
            updateProjectDetailsByTitle(
              prev,
              title,
              editedValue,
              selectedValuesUpdateProject,
            ),
          );

          setOpenDetailsDialog(false);
          console.log("✅ Project updated successfully");
        }
      } else if (isVariantUpdate) {
        const reqObj = {
          project_id: Number.parseInt(selectedVariantDetails?.project_id),
          variant_id: Number.parseInt(selectedVariantDetails?.variant_id),
          vehicle_functions: selectedValuesUpdateProject[title]?.map(
            (d) => d?.value,
          ),
        };
        console.log("---- reqObj Variant Vehicle Fnction---", reqObj);

        const resp = await Promise.resolve(dispatch(
          HandleApiActions({
            ...projectConfigAPI.update_vehicle_function,
            params: reqObj,
            show_toast: true,
          }),
        ));
        if (!resp?.error) {
          let projectVariantResp = await fetchProjectAndVariantDetails();

          console.log(
            "---projectVariantResp---",
            projectVariantResp.variantData,
          );






          const variantList = Array.isArray(projectVariantResp?.variantData)
            ? projectVariantResp.variantData
            : [];
          setSelectedVariantDetails(
            variantList.find(
              (v) => v.variant_id === selectedVariantDetails?.variant_id,
            ) || null,
          );

          setOpenDetailsDialog(false);
        }
      }
    } catch (error) {
      console.error("❌ Error in handleClickEdit:", error);
    }
  };
  useEffect(() => {
    setExportProjectDetails(
      buildFilteredProjectExportDetails(projectDetailsSelected),
    );
    setExportProjectVariantsDetails(
      buildFilteredVariantExportDetails(variantDetailsSelected),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetailsSelected, variantInfo]);

  const handleNextWorkflowDialog = () => {
    const isProjectArchived = projectDetailsSelected?.archive_status === "ARCHIVED";
    const target = resolveWorkflowDialogTarget(workflowOption, isProjectArchived);
    setArchiveTarget(target);
    if (target === "variant") {
      setOpenArchiveVariantDialog(true);
    } else {
      setOpenArchiveOptionDialog(true);
    }
    setOpenWorkflowDialog(false);
  };

  const handleArchiveOptionSuccess = async () => {
    if (archiveTarget === "project") {
      const reqObj = { project_id: [projectDetailsSelected?.project_id] };
      const apiAction = workflowOption === "archive" ? projectConfigAPI.archive_project : projectConfigAPI.retrieve_project;
      if (apiAction) {
        const resp = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...apiAction,
              params: reqObj,
              show_toast: true,
            })
          )
        );
        if (!resp?.error) {
          const newStatus = workflowOption === "archive" ? "ARCHIVED" : "ACTIVE";
          setProjectDetailsSelected((prev) =>
            prev ? { ...prev, archive_status: newStatus } : null,
          );
          void fetchProjectAndVariantDetails();
          setOpenArchiveOptionDialog(false);
        }
      }
    } else {
      setOpenArchiveOptionDialog(false);
      setOpenArchiveVariantDialog(true);
    }
  };

  const handleArchiveVariantSuccess = async () => {
    if (selectedVariantsToArchive.length === 0) {
      showToastMessage({ type: "error", message: "Please select at least one variant." });
      return;
    }
    const reqObj = {
      project_id: projectDetailsSelected?.project_id,
      variant_id: selectedVariantsToArchive
    };
    const apiAction = workflowOption === "archive" ? projectConfigAPI.archive_variant : projectConfigAPI.retrieve_variant;
    
    if (apiAction) {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiAction,
            params: reqObj,
            show_toast: true,
          })
        )
      );
      if (!resp?.error) {
        const newStatus = workflowOption === "archive" ? "ARCHIVED" : "ACTIVE";
        setSelectedVariantDetails((prev) => prev ? { ...prev, archive_status: newStatus } : null);
        setVariantDetailsSelected((prev) => prev.map(v => 
          selectedVariantsToArchive.includes(v.variant_id) ? { ...v, archive_status: newStatus } : v
        ));
        await fetchProjectAndVariantDetails();
        setOpenArchiveVariantDialog(false);
        setSelectedVariantsToArchive([]);
      }
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <ProjectConfigHeader
        onBack={() => navigate("/dashboard/project-config")}
        exportProjectDetails={exportProjectDetails}
        exportProjectVariantsDetails={exportProjectVariantsDetails}
      />
      <ProjectConfigMainLayout
        tabsDataProject={tabsDataProject}
        expandPC={expandPC}
        handleExpandPC={handleExpandPC}
        handleEditClick={handleEditClick}
        logsVariant={logsVariant}
        expandedIndex={expandedIndex}
        setExpandedIndex={setExpandedIndex}
        variantDetailsSelected={variantDetailsSelected}
        expandVC={expandVC}
        handleExpandVC={handleExpandVC}
        handleVariantToggle={handleVariantToggle}
        handleAddVariant={handleAddVariant}
        loading={loading}
        handleExpandVCLogs={handleExpandVCLogs}
        expandVCLogs={expandVCLogs}
        vehicleStatusData={vehicleStatusData}
        logsCountVC={logsCountVC}
        rowsPerPageVCLogCount={rowsPerPageVCLogCount}
        pageVCLogCount={pageVCLogCount}
        handlePageChangeLogsVC={handlePageChangeLogsVC}
        isRecreatedProject={isRecreatedProject}
        handleRecreateVariantClick={handleRecreateVariantClick}
        handleBackToPC={handleBackToPC}
        tabsData1={tabsData1}
        tableProjectConfigs={tableProjectConfigs}
        projectId={projectDetailsSelected?.project_id || projectId}
        handleCopyVariant={handleCopyVariant}
        handleOpenWorkflowDialog={() => setOpenWorkflowDialog(true)}
        projectDetailsSelected={projectDetailsSelected}
        setWorkflowOption={setWorkflowOption}
        setArchiveTarget={setArchiveTarget}
        selectedVariantDetails={selectedVariantDetails}
        setSelectedVariantsToArchive={setSelectedVariantsToArchive}
        setOpenArchiveVariantConfirmDialog={setOpenArchiveVariantConfirmDialog}
        renderBeforeZones={
          projectDetailsSelected?.status === PROJECT_STATUS.DRAFT ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                width: "100%",
                gap: 1,
                mb: 0.5,
                px: 1,
              }}
            >
              <CommonInputSwitch
                checked={enableRetrievalInVlc}
                onChange={handleToggleEnableRetrievalInVlc}
                label="Enable Retrival in VLC"
              />
              <Typography
                sx={{
                  fontFamily: "Open Sans",
                  fontSize: "14px",
                  minWidth: "30px",
                }}
              >
                {enableRetrievalInVlc ? "Yes" : "No"}
              </Typography>
            </Box>
          ) : null
        }
      />
      {console.log(
        "selectedValuesUpdateProject[dialogTitleProject]",
        selectedValuesUpdateProject[dialogTitleProject],
      )}
      {openAddVariant && (
        <AddDialog
          disabled={isAddDisabledVC}
          componentMap={componentMap}
          radioOptions={radioOptions}
          formLayout={formLayout}
          singleColumnRows={singleColumnRows}
          open={openAddVariant}
          onClose={() => handleOpenCloseAddVariant(false)}
          title={"Create Variant"}
          handleClickAddDialog={handleClickAddDialogVariant}
        />
      )}
      {openRecreationVariant && (
        <AddDialog
          disabled={isAddDisabledVC}
          componentMap={componentMap}
          radioOptions={radioOptions}
          formLayout={formLayout}
          singleColumnRows={[1, 2, 3, 5, 6, 7, 8, 9, 10]}
          open={openRecreationVariant}
          onClose={() => {
            handleCloseRecreateVariant(false);
            handleOpenCloseAddVariant(false);
          }}
          title={"Re-create Variant"}
          handleClickAddDialog={handleClickRecreateDialogVariant}
        />
      )}
      {openUpdateMilestoneDialog && (
        <AddDialog
          disabled={isUpdateMilestoneDisabled}
          componentMap={componentMap}
          radioOptions={radioOptions}
          formLayout={updateMilestoneFormLayout}
          singleColumnRows={[1]}
          open={openUpdateMilestoneDialog}
          onClose={handleCloseUpdateMilestoneDialog}
          title={"Update Milestone"}
          titleAlign="left"
          hideDividers
          handleClickAddDialog={handleUpdateMilestones}
        />
      )}
      {openDetailsDialog && (
        <CustomDialogBoxV2
          open={openDetailsDialog}
          onClose={handleCloseConfigDetailsDialog}
          title={`Add ${formatDialogTitle(dialogTitleProject)}`}
          successButtonName="Add"
          rejectButtonName="Cancel"
          otherButtonName="Re-direct"
          actionButtonStyle={"continuous"}
          onClickSuccess={handleConfirmDetailsSubmit}
          onClickReject={handleCloseConfigDetailsDialog}
          onOtherButtonClick={() => { }}
          onDisabled={
            !selectedValuesUpdateProject[dialogTitleProject]?.length ||
            areSelectionArraysEqual(
              selectedValuesUpdateProject[dialogTitleProject] || [],
              initialDetailsDialogValues || [],
            )
          }
        >
          {(dialogTitleProject === "POWERTRAIN CLASS" ||
            dialogTitleProject === "VEHICLE FUNCTION(PROJECT)" ||
            dialogTitleProject === "VEHICLE FUNCTION(VARIANT)" ||
            dialogTitleProject === "MANUFACTURER CONFIGURATIONS" ||
            dialogTitleProject === "DELIVERY MARKET") && (
              <Box sx={{ width: "600px" }}>
                <CustomMultiSelectV2
                  selectedValues={selectedValuesUpdateProject[dialogTitleProject]}
                  setSelectedValues={handleSelectionChange}
                  optionsList={getOptionsList(
                    selectedValuesUpdateProject[dialogTitleProject],
                  )}
                  {...(dialogTitleProject === "MANUFACTURER CONFIGURATIONS" && {
                    renderOption: (props, option) => (
                      <li {...props} key={option.value?.id || option.label}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography sx={{ fontWeight: 400 }}>
                            {option.label}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                          >
                            {option.subLabel}
                          </Typography>
                        </Box>
                      </li>
                    ),
                    getOptionLabel: (option) => option.label || "",
                  })}
                />
              </Box>
            )}
        </CustomDialogBoxV2>
      )}
      {openArchitectureDialog && (
        <CustomDialogBoxV2
          open={openArchitectureDialog}
          onClose={() => setOpenArchitectureDialog(false)}
          title="Edit Architecture"
          successButtonName="Update"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={handleConfirmArchitectureSubmit}
          onClickReject={() => setOpenArchitectureDialog(false)}
          onDisabled={
            !architectureSelected ||
            (architectureSelected?.toUpperCase() === "ZONAL" && zonesSelected?.length === 0) ||
            (architectureSelected === initialArchitectureValues.architecture &&
              (architectureSelected?.toUpperCase() !== "ZONAL" ||
                areSelectionArraysEqual(zonesSelected, initialArchitectureValues.zones)))
          }
        >
          <Box sx={{ width: "500px", display: "flex", flexDirection: "column", gap: 2 }}>
            <CustomRadioButtonV2
              label="Architecture"
              required
              value={architectureSelected}
              onChange={(e) => {
                setArchitectureSelected(e.target.value);
                if (e.target.value?.toUpperCase() !== "ZONAL") {
                  setZonesSelected([]);
                }
              }}
              data={[
                { label: "Centralized", value: "CENTRALIZED" },
                { label: "Zonal", value: "ZONAL" },
              ]}
            />
            {architectureSelected?.toUpperCase() === "ZONAL" && (
              <CustomMultiSelectV2
                label="Zone Name"
                required
                selectedValues={zonesSelected}
                setSelectedValues={setZonesSelected}
                optionsList={(adminDefaultData?.data?.zone_details || []).map((item) => ({
                  label: item?.position_name,
                  value: item?.zone_position,
                  sequence: item?.sequence,
                }))}
              />
            )}
          </Box>
        </CustomDialogBoxV2>
      )}
      {openVariantDeliveryMarketDialog && (
        <CustomDialogBoxV2
          open={openVariantDeliveryMarketDialog}
          onClose={() => setOpenVariantDeliveryMarketDialog(false)}
          title="Edit Delivery Market"
          successButtonName="Update"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={handleConfirmVariantDeliveryMarketSubmit}
          onClickReject={() => setOpenVariantDeliveryMarketDialog(false)}
          onDisabled={!selectedVariantDeliveryMarket}
        >
          <Box sx={{ width: "400px", display: "flex", flexDirection: "column", gap: 2 }}>
            <CustomSelectV2
              label="Delivery Market"
              required
              handleOnChange={(e) => setSelectedVariantDeliveryMarket(e.target.value)}
              selectedValue={selectedVariantDeliveryMarket}
              optionsList={(projectDetailsSelected?.delivery_market || []).map((item) => ({
                label: item,
                value: item,
              }))}
            />
          </Box>
        </CustomDialogBoxV2>
      )}
      {openVariantPowertrainDialog && (
        <CustomDialogBoxV2
          open={openVariantPowertrainDialog}
          onClose={() => setOpenVariantPowertrainDialog(false)}
          title="Edit Powertrain Class"
          successButtonName="Update"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={handleConfirmVariantPowertrainSubmit}
          onClickReject={() => setOpenVariantPowertrainDialog(false)}
          onDisabled={!selectedVariantPowertrain}
        >
          <Box sx={{ width: "400px", display: "flex", flexDirection: "column", gap: 2 }}>
            <CustomSelectV2
              label="Powertrain Class"
              required
              handleOnChange={(e) => setSelectedVariantPowertrain(e.target.value)}
              selectedValue={selectedVariantPowertrain}
              optionsList={(projectDetailsSelected?.powertrain_class_details || []).map((item) => ({
                label: item.name,
                value: item.id,
              }))}
            />
          </Box>
        </CustomDialogBoxV2>
      )}
      {openVariantManufacturerDialog && (
        <CustomDialogBoxV2
          open={openVariantManufacturerDialog}
          onClose={() => setOpenVariantManufacturerDialog(false)}
          title="Edit Manufacture Name"
          successButtonName="Update"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={handleConfirmVariantManufacturerSubmit}
          onClickReject={() => setOpenVariantManufacturerDialog(false)}
          onDisabled={!selectedVariantManufacturer}
        >
          <Box sx={{ width: "400px", display: "flex", flexDirection: "column", gap: 2 }}>
            <CustomSelectV2
              label="Manufacture Name"
              required
              handleOnChange={(e) => setSelectedVariantManufacturer(e.target.value)}
              selectedValue={selectedVariantManufacturer}
              optionsList={(projectDetailsSelected?.mft_details || []).map((item) => ({
                label: item.name,
                value: item.name,
              }))}
            />
          </Box>
        </CustomDialogBoxV2>
      )}
      {openWorkflowDialog && (
        <CustomDialogBoxV2
          open={openWorkflowDialog}
          onClose={() => setOpenWorkflowDialog(false)}
          title="Select Work Flow"
          dialogRadius="0rem"
          successButtonName="Next"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={handleNextWorkflowDialog}
          onClickReject={() => setOpenWorkflowDialog(false)}
        >
          <Box sx={{ width: "400px", display: "flex", flexDirection: "column", gap: 2 }}>
            <CustomRadioButtonV2
              value={workflowOption}
              onChange={(e) => setWorkflowOption(e.target.value)}
              data={[
                { label: "Archive", value: "archive" },
                { label: "Retrieve", value: "retrieve" },
              ]}
            />
          </Box>
        </CustomDialogBoxV2>
      )}
      {openArchiveOptionDialog && (
        <CustomDialogBoxV2
          open={openArchiveOptionDialog}
          onClose={() => setOpenArchiveOptionDialog(false)}
          title={workflowOption === "archive" ? "Archive" : "Retrieve"}
          dialogRadius="0rem"
          successButtonName="Next"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={handleArchiveOptionSuccess}
          onClickReject={() => setOpenArchiveOptionDialog(false)}
        >
          <Box sx={{ width: "400px", display: "flex", flexDirection: "column", gap: 2 }}>
            <CustomRadioButtonV2
              value={archiveTarget}
              onChange={(e) => setArchiveTarget(e.target.value)}
              data={[
                { label: "Project", value: "project" },
                { label: "Variant", value: "variant" },
              ]}
            />
          </Box>
        </CustomDialogBoxV2>
      )}
      <ArchiveVariantDialog
        open={openArchiveVariantDialog}
        onClose={() => setOpenArchiveVariantDialog(false)}
        workflowOption={workflowOption}
        variantDetailsSelected={variantDetailsSelected}
        selectedVariantsToArchive={selectedVariantsToArchive}
        setSelectedVariantsToArchive={setSelectedVariantsToArchive}
        onClickSuccess={handleArchiveVariantSuccess}
      />
      {openArchiveVariantConfirmDialog && (
        <CustomDialogBoxV2
          open={openArchiveVariantConfirmDialog}
          onClose={() => setOpenArchiveVariantConfirmDialog(false)}
          title={`Confirm ${workflowOption === "archive" ? "Archive" : "Retrieve"}`}
          dialogRadius="0rem"
          successButtonName="Confirm"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={() => {
            setOpenArchiveVariantConfirmDialog(false);
            void handleArchiveVariantSuccess();
          }}
          onClickReject={() => setOpenArchiveVariantConfirmDialog(false)}
        >
          <Box sx={{  p: 2, display: "flex", flexDirection: "column", alignItems: "left", textAlign: "left", gap: 1 }}>
            <Typography>
                This change will affect the downstream components, modules, dependencies and intersections, if any are linked to the element.
            </Typography>
            <Typography>
              Are you sure you want to {workflowOption === "archive" ? "archive" : "retrieve"} this variant?
            </Typography>
          </Box>
        </CustomDialogBoxV2>
      )}
      {openRemoveConfirmDialog && (
        <CustomDialogBoxV2
          open={openRemoveConfirmDialog}
          onClose={() => setOpenRemoveConfirmDialog(false)}
          title="Confirm Changes"
          successButtonName="Confirm"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={() => {
            setOpenRemoveConfirmDialog(false);
            if (pendingConfirmAction === "architecture") {
              void handleSaveArchitecture();
            } else if (pendingConfirmAction === "variant_delivery_market") {
              void handleSaveVariantDeliveryMarket();
            } else if (pendingConfirmAction === "variant_powertrain") {
              void handleSaveVariantPowertrain();
            } else if (pendingConfirmAction === "variant_manufacturer") {
              void handleSaveVariantManufacturer();
            } else {
              void handleClickEdit(dialogTitleProject?.toUpperCase());
            }
          }}
          onClickReject={() => setOpenRemoveConfirmDialog(false)}
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "left", textAlign: "left", gap: 1 }}>
            <Typography>
              This change will affect the downstream components, modules, dependencies and intersections, if any are linked to the element.
            </Typography>
            <Typography>
              Are you sure you want to proceed?
            </Typography>
          </Box>
        </CustomDialogBoxV2>
      )}
    </Box>
  );
}

ProjectConfigHeader.propTypes = {
  onBack: PropTypes.func,
  exportProjectDetails: PropTypes.array,
  exportProjectVariantsDetails: PropTypes.array,
};
ProjectConfigMainLayout.propTypes = {
  tabsDataProject: PropTypes.array,
  expandPC: PropTypes.bool,
  handleExpandPC: PropTypes.func,
  handleEditClick: PropTypes.func,
  logsVariant: PropTypes.array,
  expandedIndex: PropTypes.number,
  setExpandedIndex: PropTypes.func,
  variantDetailsSelected: PropTypes.array,
  expandVC: PropTypes.bool,
  handleExpandVC: PropTypes.func,
  handleVariantToggle: PropTypes.func,
  handleAddVariant: PropTypes.func,
  handlePageChangeLogsVC: PropTypes.func,
  pageVCLogCount: PropTypes.number,
  rowsPerPageVCLogCount: PropTypes.number,
  expandVCLogs: PropTypes.any,
  loading: PropTypes.bool,
  vehicleStatusData: PropTypes.any,
  handleCopyVariant: PropTypes.func,
  isRecreatedProject: PropTypes.bool,
  
  handleExpandVCLogs: PropTypes.func,
  logsCountVC: PropTypes.number,
  handleRecreateVariantClick: PropTypes.func,
  handleBackToPC: PropTypes.func,
  tabsData1: PropTypes.array,
  tableProjectConfigs: PropTypes.array,
  projectId: PropTypes.any,
  handleOpenWorkflowDialog: PropTypes.func,
  projectDetailsSelected: PropTypes.object,
  setWorkflowOption: PropTypes.func,
  setArchiveTarget: PropTypes.func,
  selectedVariantDetails: PropTypes.object,
  setSelectedVariantsToArchive: PropTypes.func,
  setOpenArchiveVariantConfirmDialog: PropTypes.func,
  renderBeforeZones: PropTypes.node,
};
MilestoneSection.propTypes = {
  rows: PropTypes.array,
  draft: PropTypes.shape({ milestoneDates: PropTypes.object }),
  onAddMilestone: PropTypes.func,
  onRemoveMilestone: PropTypes.func,
  milestoneNameOptions: PropTypes.array,
};
ViewProjectConfig.propTypes = {};
export default ViewProjectConfig;
